import {
  StrictMode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";
import type { FormEvent, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  ArrowRight,
  BookOpen,
  Bot,
  CalendarClock,
  Check,
  ChevronDown,
  CheckCircle2,
  Clock3,
  CreditCard,
  Download,
  EyeOff,
  Gauge,
  Gift,
  History,
  LockKeyhole,
  LogIn,
  LogOut,
  MessageCircle,
  Pencil,
  Pin,
  PlusCircle,
  RefreshCw,
  ScrollText,
  Search,
  Send,
  Server as ServerIcon,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Upload,
  X,
  Zap,
} from "lucide-react";
import {
  ApiError,
  askKodama,
  createBillingPortalSession,
  createCheckoutSession,
  createScanCheckoutSession,
  createLore,
  deleteLore,
  exchangeOAuthCode,
  exportMyServerData,
  fetchScanLogs,
  getAdminServers,
  getAdminStats,
  getAdminSubscriptions,
  getAdminUsage,
  type AdminSubscription,
  type AdminGift,
  getMe,
  getServer,
  getStoredToken,
  initiateLogin,
  logout,
  scanServerHistory,
  fullScanServer,
  cancelScan,
  fetchServerChannels,
  storeToken,
  updateSettings,
  listChannelExclusions,
  addChannelExclusion,
  deleteChannelExclusion,
  type AdminServer,
  type AdminStats,
  type AdminUsageData,
  type AuditEvent,
  type AskMessage,
  type ChannelExclusion,
  type GuildChannel,
  type LoreEntry,
  type MeResponse,
  type ManageableGuild,
  type ScanLog,
  type ScanLogSummary,
  type ScanLifecycle,
  type ServerDetail,
  type ServerSummary,
  type SettingsPatch,
} from "./api";
import "./styles.css";

const marketingOrigin = "https://askkodama.com";

type TabId =
  | "overview"
  | "memory"
  | "recaps"
  | "privacy"
  | "settings"
  | "profile"
  | "logs";

type PageId =
  | "servers"
  | "dashboard"
  | "store"
  | "auth-finish"
  | "oauth-callback"
  | "admin";

const defaultBotAvatarUrl = "/default_bot_pfp.png";
const defaultBannerUrl = "/default_banner.png";
const maxAvatarBytes = 1024 * 1024;

const tabs: Array<{ id: TabId; label: string; icon: typeof Gauge }> = [
  { id: "overview", label: "Overview", icon: Gauge },
  { id: "memory", label: "Memory", icon: Search },
  { id: "profile", label: "Bot Profile", icon: Bot },
  { id: "recaps", label: "Schedule", icon: CalendarClock },
  { id: "settings", label: "Controls", icon: SlidersHorizontal },
  { id: "privacy", label: "Guardrails", icon: ShieldCheck },
  { id: "logs", label: "Logs", icon: ScrollText },
];

function botInviteUrl(guildId?: string): string {
  const params = new URLSearchParams({
    client_id: "1508256939547561995",
    permissions: "67456064", // View Channel + Read History + Send Messages + Add Reactions + Embed Links + External Emojis + Change Nickname
    scope: "bot applications.commands",
  });
  if (guildId) {
    params.set("guild_id", guildId);
    params.set("disable_guild_select", "true");
  }
  return `https://discord.com/oauth2/authorize?${params.toString()}`;
}

function currentReturnTo(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getInitialTab(): TabId {
  const hash = window.location.hash.replace("#", "");
  return tabs.some((tab) => tab.id === hash) ? (hash as TabId) : "overview";
}

function getInitialPage(): PageId {
  if (window.location.pathname.startsWith("/auth/finish")) return "auth-finish";
  if (window.location.pathname.startsWith("/api/auth/callback"))
    return "oauth-callback";
  if (window.location.pathname.startsWith("/store")) return "store";
  if (window.location.pathname.startsWith("/admin")) return "admin";
  if (window.location.pathname.startsWith("/usage")) {
    window.history.replaceState(null, "", "/admin?tab=usage");
    return "admin";
  }
  if (window.location.pathname.startsWith("/servers")) return "servers";
  if (window.location.pathname.startsWith("/dashboard")) return "dashboard";
  // Any unrecognised path on app.askkodama.com → send to the marketing site
  if (
    !window.location.pathname.startsWith("/servers") &&
    !window.location.pathname.startsWith("/dashboard")
  ) {
    window.location.replace(
      `${marketingOrigin}${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
  }
  return "servers";
}

function formatDate(value: string | null): string {
  if (!value) return "No messages yet";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatAction(action: string): string {
  const labels: Record<string, string> = {
    data_export: "Data export",
    full_scan: "Full scan finished",
    full_scan_started: "Full scan started",
    history_scan: "History scan finished",
    history_scan_started: "History scan started",
    scan_cancel_requested: "Scan cancel requested",
    settings_change: "Settings changed",
    channel_exclusion_added: "Channel excluded",
    channel_exclusion_removed: "Channel restored",
    lore_add: "Memory pinned",
    lore_delete: "Memory deleted",
    lore_promote: "Report promoted",
    optin: "Opted in",
    optout: "Opted out",
    forget: "Message forgotten",
  };
  if (labels[action]) return labels[action];
  return action
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatCategory(category: string | null): string {
  if (
    !category ||
    category === "inside_joke" ||
    category === "moment" ||
    category === "lore"
  )
    return "Lore";
  return formatAction(category);
}

// ---------------------------------------------------------------------------
// ConfirmModal — reusable in-app confirmation dialog (replaces window.confirm)
// ---------------------------------------------------------------------------
function ConfirmModal({
  message,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  danger = true,
  onConfirm,
  onCancel,
}: {
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Close on backdrop click
  function handleBackdrop(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onCancel();
  }
  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="confirm-backdrop" onClick={handleBackdrop}>
      <div className="confirm-modal" role="dialog" aria-modal="true">
        <p className="confirm-message">{message}</p>
        <div className="confirm-actions">
          <button type="button" className="secondary-button" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? "danger-button" : "primary-button"}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon ?? <Bot size={24} aria-hidden="true" />}
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

function Toast({
  message,
  icon,
  tone = "neutral",
  dismissing,
  onDismiss,
}: {
  message: string;
  icon?: ReactNode;
  tone?: "neutral" | "success" | "error";
  dismissing: boolean;
  onDismiss: () => void;
}) {
  const toneClass = tone === "neutral" ? "" : ` toast-${tone}`;
  return (
    <button
      className={`toast${toneClass}${dismissing ? " toast-leaving" : ""}`}
      type="button"
      role="alert"
      onClick={onDismiss}
    >
      {icon}
      <span>{message}</span>
    </button>
  );
}

function AuthErrorToast({
  dismissing,
  onDismiss,
}: {
  dismissing: boolean;
  onDismiss: () => void;
}) {
  return (
    <Toast
      message="Discord sign-in could not start. Check that the dashboard API URL is live and allowed by CORS, then try again."
      tone="error"
      icon={<LogIn size={18} aria-hidden="true" />}
      dismissing={dismissing}
      onDismiss={onDismiss}
    />
  );
}

function UpgradeModal({
  requiredTier,
  onClose,
  onGoToStore,
}: {
  requiredTier?: string;
  onClose: () => void;
  onGoToStore: () => void;
}) {
  const tierLabel = requiredTier === "chronicle" ? "Chronicle" : "Index";
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="upgrade-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="modal-close"
          type="button"
          aria-label="Close"
          onClick={onClose}
        >
          ✕
        </button>
        <div className="upgrade-modal-header">
          <strong>{tierLabel} required</strong>
          <p>this feature needs Kodama {tierLabel} or higher</p>
        </div>
        <div className="upgrade-modal-tiers">
          <div className="upgrade-tier-card upgrade-tier-free">
            <strong>Free</strong>
            <span className="upgrade-tier-price">$0</span>
            <ul>
              <li>Kodama interactions</li>
              <li>Privacy controls</li>
              <li>Ask Kodama 5/day</li>
            </ul>
          </div>
          <div
            className={`upgrade-tier-card upgrade-tier-index ${requiredTier === "index" || requiredTier === "chronicle" ? "upgrade-tier-highlight" : ""}`}
          >
            <strong>Index</strong>
            <span className="upgrade-tier-price">$9.99/mo</span>
            <ul>
              <li>Organic replies</li>
              <li>Server scan</li>
              <li>Daily recaps</li>
              <li>/whois profiles</li>
              <li>Bot profile & banner</li>
              <li>Ask Kodama 20/day</li>
            </ul>
          </div>
          <div
            className={`upgrade-tier-card upgrade-tier-chronicle ${requiredTier === "chronicle" ? "upgrade-tier-highlight" : ""}`}
          >
            <strong>Chronicle</strong>
            <span className="upgrade-tier-price">$19.99/mo</span>
            <ul>
              <li>Image & GIF reading</li>
              <li>
                No 20/day Kodama interaction cap for anyone in servers you own
                or manage
              </li>
              <li>Ask Kodama unlimited</li>
              <li>Full history scan</li>
              <li>Inside joke detection</li>
              <li>Reports feed</li>
            </ul>
          </div>
        </div>
        <div className="upgrade-modal-actions">
          <button className="ghost-button" type="button" onClick={onClose}>
            maybe later
          </button>
          <button
            className="primary-button"
            type="button"
            onClick={onGoToStore}
          >
            <CreditCard size={16} aria-hidden="true" />
            view plans
          </button>
        </div>
      </div>
    </div>
  );
}

function ServerSelect({
  servers,
  selectedId,
  fallbackSelected,
  onChange,
}: {
  servers: ServerSummary[];
  selectedId: string | null;
  /** Used when the selected server isn't in the user's own list (e.g. admin viewing another server). */
  fallbackSelected?: ServerSummary | null;
  onChange: (serverId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [closing, setClosing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const selected =
    servers.find((server) => server.id === selectedId) ??
    fallbackSelected ??
    undefined;

  function openMenu(): void {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setRenderMenu(true);
    setClosing(false);
    setOpen(true);
  }

  function closeMenu(): void {
    setOpen(false);
    setClosing(true);
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setRenderMenu(false);
      setClosing(false);
      closeTimerRef.current = null;
    }, 160);
  }

  function toggleMenu(): void {
    if (open) {
      closeMenu();
    } else {
      openMenu();
    }
  }

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        closeMenu();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  });

  return (
    <div ref={ref} className="server-picker">
      <span className="server-picker-label">Server</span>
      <button
        className="server-picker-trigger"
        type="button"
        aria-expanded={open}
        onClick={toggleMenu}
      >
        <span className="server-avatar" aria-hidden="true">
          {selected?.icon_url ? (
            <img src={selected.icon_url} alt="" />
          ) : (
            (selected?.name ?? "K").slice(0, 2).toUpperCase()
          )}
        </span>
        <strong>{selected?.name ?? "Select server"}</strong>
        <ChevronDown
          size={18}
          aria-hidden="true"
          className={open ? "chevron-open" : ""}
        />
      </button>
      {renderMenu && (
        <div
          className={`server-picker-menu dropdown-menu ${
            closing ? "dropdown-closing" : "dropdown-opening"
          }`}
          role="listbox"
        >
          {servers.map((server) => (
            <button
              className={`server-picker-option ${
                server.id === selectedId ? "active" : ""
              }`}
              key={server.id}
              type="button"
              role="option"
              aria-selected={server.id === selectedId}
              onClick={() => {
                onChange(server.id);
                closeMenu();
              }}
            >
              <span className="server-avatar" aria-hidden="true">
                {server.icon_url ? (
                  <img src={server.icon_url} alt="" />
                ) : (
                  server.name.slice(0, 2).toUpperCase()
                )}
              </span>
              <span>
                <strong>{server.name}</strong>
                <small>{server.personality}</small>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

type SelectOption = {
  value: string;
  label: string;
  detail?: string;
};

function CustomSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [closing, setClosing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const selected =
    options.find((option) => option.value === value) ?? options[0];

  function openMenu(): void {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setRenderMenu(true);
    setClosing(false);
    setOpen(true);
  }

  function closeMenu(): void {
    setOpen(false);
    setClosing(true);
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setRenderMenu(false);
      setClosing(false);
      closeTimerRef.current = null;
    }, 160);
  }

  function toggleMenu(): void {
    if (open) {
      closeMenu();
    } else {
      openMenu();
    }
  }

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        closeMenu();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  });

  return (
    <div ref={ref} className="custom-select">
      <button
        className="custom-select-trigger"
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={toggleMenu}
      >
        <span>
          <strong>{selected?.label ?? "Select"}</strong>
          {selected?.detail && <small>{selected.detail}</small>}
        </span>
        <ChevronDown
          size={18}
          aria-hidden="true"
          className={open ? "chevron-open" : ""}
        />
      </button>
      {renderMenu && (
        <div
          className={`custom-select-menu dropdown-menu ${
            closing ? "dropdown-closing" : "dropdown-opening"
          }`}
          role="listbox"
        >
          {options.map((option) => (
            <button
              className={`custom-select-option ${
                option.value === value ? "active" : ""
              }`}
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                closeMenu();
              }}
            >
              <strong>{option.label}</strong>
              {option.detail && <small>{option.detail}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  detail,
  tone = "neutral",
}: {
  label: string;
  value: string;
  detail: string;
  tone?: "neutral" | "good" | "warn";
}) {
  return (
    <article className={`metric tone-${tone}`}>
      <p>{label}</p>
      <strong>{value}</strong>
      <span>{detail}</span>
    </article>
  );
}

function SectionHeader({
  eyebrow,
  title,
  aside,
}: {
  eyebrow: string;
  title: string;
  aside?: ReactNode;
}) {
  return (
    <div className="panel-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h3>{title}</h3>
      </div>
      {aside}
    </div>
  );
}

function StatusPill({
  children,
  tone = "good",
}: {
  children: ReactNode;
  tone?: "good" | "muted" | "warn" | "info";
}) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

function meetsRequiredTier(
  current: string,
  required: "index" | "chronicle",
): boolean {
  if (required === "index")
    return current === "index" || current === "chronicle";
  return current === "chronicle";
}

function effectiveDashboardTier(
  me: MeResponse | null,
  detail: ServerDetail,
): string {
  return me?.isAdmin ? "chronicle" : (detail.userTier ?? "free");
}

function LockedSection({
  requiredTier,
  currentTier,
  onUpgrade,
  children,
}: {
  requiredTier: "index" | "chronicle";
  currentTier: string;
  onUpgrade: () => void;
  children: ReactNode;
}) {
  if (meetsRequiredTier(currentTier, requiredTier)) return <>{children}</>;
  const tierLabel =
    requiredTier === "index" ? "Kodama Index" : "Kodama Chronicle";
  return (
    <div className="lockable-section">
      <div className="lockable-content" aria-hidden="true">
        {children}
      </div>
      <button
        type="button"
        className="locked-overlay"
        onClick={onUpgrade}
        aria-label={`Upgrade to ${tierLabel} to unlock`}
      >
        <LockKeyhole size={28} aria-hidden="true" />
        <strong>{tierLabel}</strong>
        <span>See plans</span>
      </button>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  detail,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  detail: string;
  onClick: () => void;
}) {
  return (
    <button className="quick-action" type="button" onClick={onClick}>
      <span>{icon}</span>
      <strong>{label}</strong>
      <small>{detail}</small>
    </button>
  );
}

function BrandBlock({
  label = "Dashboard",
  onNavigate,
}: {
  label?: string;
  onNavigate?: () => void;
}) {
  return (
    <button
      type="button"
      className="brand brand-button-reset"
      onClick={onNavigate}
      style={{
        cursor: onNavigate ? "pointer" : "default",
        background: "none",
        border: "none",
        padding: 0,
        textAlign: "left",
      }}
    >
      <span className="brand-mark">
        <img src="/kodama_logo.png" alt="Kodama home" />
      </span>
      <div>
        <p className="eyebrow">Kodama</p>
        <h1>{label}</h1>
      </div>
    </button>
  );
}

function UserMenu({
  displayName,
  avatarUrl,
  isAdmin,
  onNavigate,
}: {
  displayName: string;
  avatarUrl: string | null;
  isAdmin?: boolean;
  onNavigate: (page: PageId) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="user-menu-root">
      <button
        type="button"
        className="user-menu-trigger"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" aria-hidden="true" />
        ) : (
          <span aria-hidden="true">{displayName.slice(0, 1)}</span>
        )}
        <strong>{displayName}</strong>
        <ChevronDown
          size={15}
          aria-hidden="true"
          className={open ? "chevron-open" : ""}
        />
      </button>
      {open && (
        <div className="user-menu-dropdown">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onNavigate("servers");
            }}
          >
            <Bot size={15} aria-hidden="true" />
            My servers
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onNavigate("store");
            }}
          >
            <CreditCard size={15} aria-hidden="true" />
            Store
          </button>
          {isAdmin && (
            <>
              <div className="user-menu-divider" />
              <button
                type="button"
                className="user-menu-admin"
                onClick={() => {
                  setOpen(false);
                  onNavigate("admin");
                }}
              >
                <ShieldCheck size={15} aria-hidden="true" />
                Admin panel
              </button>
            </>
          )}
          <div className="user-menu-divider" />
          <button
            type="button"
            className="user-menu-signout"
            onClick={() => logout().then(() => window.location.reload())}
          >
            <LogOut size={15} aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MarketingNav({
  me,
  meLoading = false,
  onNavigate,
}: {
  me: MeResponse | null;
  meLoading?: boolean;
  onNavigate: (page: PageId) => void;
}) {
  const displayName = me?.user.global_name ?? me?.user.username ?? "User";
  const avatarUrl = me?.user.avatar
    ? `https://cdn.discordapp.com/avatars/${me.user.id}/${me.user.avatar}.png?size=64`
    : null;

  return (
    <header className="marketing-nav">
      <a className="brand-button" href={marketingOrigin}>
        <span className="public-brand">
          <img src="/kodama_logo.png" alt="" aria-hidden="true" />
          <strong>Kodama</strong>
        </span>
      </a>
      <nav aria-label="Public navigation">
        <a href={`${marketingOrigin}/commands`}>
          <BookOpen size={17} aria-hidden="true" />
          Commands
        </a>
        <a href={`${marketingOrigin}/info`}>
          <Activity size={17} aria-hidden="true" />
          Status
        </a>
        <button
          type="button"
          onClick={() => {
            if (me) {
              onNavigate("store");
            } else {
              void initiateLogin("/store");
            }
          }}
        >
          <CreditCard size={17} aria-hidden="true" />
          Store
        </button>
        <a href={botInviteUrl()}>
          <PlusCircle size={17} aria-hidden="true" />
          Invite
        </a>
      </nav>
      <div className="marketing-user">
        {meLoading && !me ? (
          <span className="nav-auth-loading" aria-label="Loading" />
        ) : me ? (
          <UserMenu
            displayName={displayName}
            avatarUrl={avatarUrl}
            isAdmin={me.isAdmin}
            onNavigate={onNavigate}
          />
        ) : (
          <button type="button" onClick={() => void initiateLogin("/servers")}>
            <LogIn size={17} aria-hidden="true" />
            Sign in
          </button>
        )}
      </div>
    </header>
  );
}

// ── Admin Panel ───────────────────────────────────────────────────────────────

type AdminSortKey = "name" | "messages" | "lore" | "members" | "installed";
type AdminSortDir = "asc" | "desc";
type AdminTab = "overview" | "servers" | "purchases" | "usage";
type UsageWindow = "24h" | "7d" | "30d" | "all";

function initialAdminTab(): AdminTab {
  const tab = new URLSearchParams(window.location.search).get("tab");
  return tab === "servers" || tab === "purchases" || tab === "usage"
    ? tab
    : "overview";
}

function AdminPage({
  me,
  meLoading = false,
  onNavigate,
  onOpenServer,
}: {
  me: MeResponse | null;
  meLoading?: boolean;
  onNavigate: (page: PageId) => void;
  onOpenServer: (serverId: string) => void;
}) {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [servers, setServers] = useState<AdminServer[]>([]);
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [gifts, setGifts] = useState<AdminGift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<AdminUsageData | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [usageWindow, setUsageWindow] = useState<UsageWindow>("24h");
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<AdminSortKey>("messages");
  const [sortDir, setSortDir] = useState<AdminSortDir>("desc");
  const [adminTab, setAdminTab] = useState<AdminTab>(initialAdminTab);
  const canAccessAdmin = me?.isAdmin === true;
  const activePurchaseCount = useMemo(
    () =>
      subscriptions.filter(
        (subscription) =>
          subscription.status === "active" ||
          subscription.status === "trialing",
      ).length,
    [subscriptions],
  );

  const loadAdminData = useCallback(() => {
    setLoading(true);
    setError(null);
    return Promise.all([
      getAdminStats(),
      getAdminServers(),
      getAdminSubscriptions(),
    ])
      .then(([s, sv, subs]) => {
        setStats(s);
        setServers(sv.servers);
        setSubscriptions(subs.subscriptions);
        setGifts(subs.gifts);
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error ? err.message : "Failed to load admin data",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const loadUsageData = useCallback((window: UsageWindow) => {
    setUsageLoading(true);
    setUsageError(null);
    return getAdminUsage(window)
      .then((usage) => {
        setUsageData(usage);
        setLastRefreshed(new Date());
      })
      .catch((err: unknown) => {
        setUsageError(
          err instanceof Error ? err.message : "Failed to load usage data",
        );
      })
      .finally(() => setUsageLoading(false));
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadAdminData(), loadUsageData(usageWindow)]);
    setLastRefreshed(new Date());
  }, [loadAdminData, loadUsageData, usageWindow]);

  useEffect(() => {
    if (!canAccessAdmin) return;
    void loadAdminData();
  }, [canAccessAdmin, loadAdminData]);

  useEffect(() => {
    if (!canAccessAdmin) return;
    void loadUsageData(usageWindow);
  }, [canAccessAdmin, loadUsageData, usageWindow]);

  useEffect(() => {
    if (!canAccessAdmin || adminTab !== "usage") return;
    const timer = window.setInterval(() => {
      void loadUsageData(usageWindow);
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [adminTab, canAccessAdmin, loadUsageData, usageWindow]);

  function selectAdminTab(tab: AdminTab): void {
    setAdminTab(tab);
    window.history.replaceState(null, "", `/admin?tab=${tab}`);
  }

  function toggleSort(key: AdminSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return servers.filter(
      (s) =>
        !q ||
        s.name.toLowerCase().includes(q) ||
        s.id.includes(q) ||
        s.tier.toLowerCase().includes(q),
    );
  }, [servers, query]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "name") cmp = a.name.localeCompare(b.name);
      else if (sortKey === "messages") cmp = a.message_count - b.message_count;
      else if (sortKey === "lore") cmp = a.lore_count - b.lore_count;
      else if (sortKey === "members") cmp = a.member_count - b.member_count;
      else if (sortKey === "installed")
        cmp = new Date(a.joined_at).getTime() - new Date(b.joined_at).getTime();
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  function SortBtn({ col, label }: { col: AdminSortKey; label: string }) {
    const active = sortKey === col;
    return (
      <button
        type="button"
        className={`admin-th-btn${active ? " active" : ""}`}
        onClick={() => toggleSort(col)}
      >
        {label}
        <span className="admin-sort-icon" aria-hidden="true">
          {active ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕"}
        </span>
      </button>
    );
  }

  if (meLoading) {
    return (
      <main className="marketing-shell">
        <MarketingNav me={me} meLoading onNavigate={onNavigate} />
        <div className="admin-shell">
          <div className="admin-loading">
            <LoadingDots label="Checking admin access" />
          </div>
        </div>
      </main>
    );
  }

  if (!canAccessAdmin) {
    return (
      <main className="marketing-shell">
        <MarketingNav me={me} meLoading={false} onNavigate={onNavigate} />
        <div className="admin-shell">
          <EmptyState
            icon={<LockKeyhole size={24} aria-hidden="true" />}
            title="Admin access required"
            body="This control center is limited to Kodama developers."
          />
        </div>
      </main>
    );
  }

  return (
    <main className="marketing-shell">
      <MarketingNav me={me} meLoading={meLoading} onNavigate={onNavigate} />
      <div className="admin-shell">
        <div className="admin-header">
          <div>
            <p className="eyebrow">Internal</p>
            <h2>Kodama Control Center</h2>
          </div>
          <div className="admin-header-actions">
            {lastRefreshed && (
              <span className="admin-updated">
                Updated {lastRefreshed.toLocaleTimeString()}
              </span>
            )}
            <button
              type="button"
              className="admin-refresh-btn"
              onClick={() => void refreshAll()}
              disabled={loading || usageLoading}
            >
              <RefreshCw
                size={14}
                aria-hidden="true"
                className={loading || usageLoading ? "is-spinning" : ""}
              />
              Refresh
            </button>
            <span className="status-pill status-good admin-badge">
              <ShieldCheck size={13} aria-hidden="true" />
              Admin access
            </span>
          </div>
        </div>
        {loading && (
          <div className="admin-loading">
            <LoadingDots label="Loading admin data" />
          </div>
        )}
        {error && <p className="admin-error">{error}</p>}
        {!loading && stats && (
          <>
            <div className="admin-tabs" role="tablist" aria-label="Admin views">
              <button
                type="button"
                role="tab"
                aria-selected={adminTab === "overview"}
                className={`admin-tab${adminTab === "overview" ? " admin-tab-active" : ""}`}
                onClick={() => selectAdminTab("overview")}
              >
                <Gauge size={14} aria-hidden="true" />
                Overview
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={adminTab === "servers"}
                className={`admin-tab${adminTab === "servers" ? " admin-tab-active" : ""}`}
                onClick={() => selectAdminTab("servers")}
              >
                <ServerIcon size={14} aria-hidden="true" />
                Servers
                <span className="admin-count">{stats.totalServers}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={adminTab === "purchases"}
                className={`admin-tab${adminTab === "purchases" ? " admin-tab-active" : ""}`}
                onClick={() => selectAdminTab("purchases")}
              >
                <CreditCard size={14} aria-hidden="true" />
                Purchases
                {activePurchaseCount > 0 && (
                  <span className="admin-count">{activePurchaseCount}</span>
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={adminTab === "usage"}
                className={`admin-tab${adminTab === "usage" ? " admin-tab-active" : ""}`}
                onClick={() => selectAdminTab("usage")}
              >
                <Activity size={14} aria-hidden="true" />
                Usage
              </button>
            </div>

            {adminTab === "overview" && (
              <div className="admin-overview-stack">
                <div className="admin-section-heading">
                  <div>
                    <p className="eyebrow">Fleet</p>
                    <h3>Community footprint</h3>
                  </div>
                  <span>
                    {stats.totalServers.toLocaleString()} tracked installations
                  </span>
                </div>
                <div className="admin-stats-grid">
                  <article className="admin-stat-card">
                    <p>Servers</p>
                    <strong>{stats.totalServers.toLocaleString()}</strong>
                    <span>{servers.length.toLocaleString()} listed below</span>
                  </article>
                  <article className="admin-stat-card admin-stat-money">
                    <p>Revenue</p>
                    <strong>
                      {stats.totalRevenueUsd == null
                        ? "—"
                        : formatUsd(stats.totalRevenueUsd, 2)}
                    </strong>
                    <span>Stripe collected total</span>
                  </article>
                  <article className="admin-stat-card">
                    <p>Messages</p>
                    <strong>{stats.totalMessages.toLocaleString()}</strong>
                    <span>Indexed total</span>
                  </article>
                  <article className="admin-stat-card">
                    <p>Lore entries</p>
                    <strong>{stats.totalLore.toLocaleString()}</strong>
                    <span>Pinned + detected</span>
                  </article>
                  <article className="admin-stat-card">
                    <p>Members</p>
                    <strong>{stats.totalMembers.toLocaleString()}</strong>
                    <span>Tracked across all servers</span>
                  </article>
                  <article className="admin-stat-card admin-stat-warn">
                    <p>Opted out</p>
                    <strong>{stats.totalOptedOut.toLocaleString()}</strong>
                    <span>Excluded from indexing</span>
                  </article>
                </div>
                <AdminOperationsSnapshot
                  usageData={usageData}
                  loading={usageLoading}
                  error={usageError}
                  servers={servers}
                  subscriptions={subscriptions}
                  activeSessions={stats.activeSessions}
                  onOpenUsage={() => selectAdminTab("usage")}
                />
              </div>
            )}
            {adminTab === "servers" && (
              <section className="admin-table-section">
                <div className="admin-section-heading">
                  <div>
                    <p className="eyebrow">Fleet</p>
                    <h3>Servers</h3>
                  </div>
                  <span>
                    {servers.length.toLocaleString()} tracked installations
                  </span>
                </div>
                <div className="admin-table-header">
                  <label className="admin-search">
                    <Search size={15} aria-hidden="true" />
                    <input
                      placeholder="Filter by name, ID or tier…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      aria-label="Filter servers"
                    />
                  </label>
                  <div className="admin-table-title">
                    <ServerIcon size={16} aria-hidden="true" />
                    <strong>Server table</strong>
                    <span className="admin-count">{servers.length}</span>
                  </div>
                </div>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th className="admin-th-server">
                          <SortBtn col="name" label="Server" />
                        </th>
                        <th>Tier</th>
                        <th>
                          <SortBtn col="messages" label="Messages" />
                        </th>
                        <th>
                          <SortBtn col="lore" label="Lore" />
                        </th>
                        <th>
                          <SortBtn col="members" label="Members" />
                        </th>
                        <th>Opted out</th>
                        <th>Last indexed</th>
                        <th>
                          <SortBtn col="installed" label="Installed" />
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.length === 0 && (
                        <tr>
                          <td colSpan={9} className="admin-empty-row">
                            No servers match
                          </td>
                        </tr>
                      )}
                      {sorted.map((server) => (
                        <tr key={server.id} className="admin-row">
                          <td className="admin-td-server">
                            <span
                              className="admin-server-icon"
                              aria-hidden="true"
                            >
                              {server.icon_url ? (
                                <img src={server.icon_url} alt="" />
                              ) : (
                                server.name.slice(0, 2).toUpperCase()
                              )}
                            </span>
                            <span className="admin-server-info">
                              <strong>{server.name}</strong>
                              <code className="admin-server-id">
                                {server.id}
                              </code>
                            </span>
                          </td>
                          <td>
                            <span
                              className={`status-pill ${server.tier === "chronicle" ? "status-good" : server.tier === "index" ? "admin-tier-index" : "status-muted"}`}
                            >
                              {server.tier}
                            </span>
                          </td>
                          <td className="admin-td-num">
                            {server.message_count.toLocaleString()}
                          </td>
                          <td className="admin-td-num">
                            {server.lore_count.toLocaleString()}
                          </td>
                          <td className="admin-td-num">
                            {server.member_count.toLocaleString()}
                          </td>
                          <td className="admin-td-num">
                            {server.opted_out_count > 0 ? (
                              <span className="admin-opted-out">
                                {server.opted_out_count}
                              </span>
                            ) : (
                              <span className="admin-td-zero">0</span>
                            )}
                          </td>
                          <td className="admin-td-date">
                            {server.last_indexed_at ? (
                              formatDate(server.last_indexed_at)
                            ) : (
                              <span className="admin-td-zero">Never</span>
                            )}
                          </td>
                          <td className="admin-td-date">
                            {formatDate(server.joined_at)}
                          </td>
                          <td className="admin-td-action">
                            <button
                              type="button"
                              className="admin-open-btn"
                              onClick={() => onOpenServer(server.id)}
                              title={`Open ${server.name} dashboard`}
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
            {adminTab === "purchases" && (
              <div className="admin-overview-stack">
                <div className="admin-section-heading">
                  <div>
                    <p className="eyebrow">Revenue</p>
                    <h3>Purchases</h3>
                  </div>
                  <span>
                    {stats.totalRevenueUsd == null
                      ? "Stripe revenue unavailable"
                      : `${formatUsd(stats.totalRevenueUsd, 2)} collected`}
                  </span>
                </div>
                <div className="admin-ops-grid">
                  <article className="admin-ops-card admin-stat-money">
                    <span>Total earned</span>
                    <strong>
                      {stats.totalRevenueUsd == null
                        ? "—"
                        : formatUsd(stats.totalRevenueUsd, 2)}
                    </strong>
                    <small>Successful USD charges minus refunds</small>
                  </article>
                  <article className="admin-ops-card">
                    <span>Paid subscriptions</span>
                    <strong>{formatNumber(activePurchaseCount)}</strong>
                    <small>Active or trialing</small>
                  </article>
                  <article className="admin-ops-card">
                    <span>Subscription records</span>
                    <strong>{formatNumber(subscriptions.length)}</strong>
                    <small>All Stripe-linked users</small>
                  </article>
                  <article className="admin-ops-card">
                    <span>Gifted access</span>
                    <strong>
                      {formatNumber(gifts.filter((gift) => gift.active).length)}
                    </strong>
                    <small>{formatNumber(gifts.length)} gift records</small>
                  </article>
                </div>

                <section className="admin-table-section">
                  <div className="admin-table-header">
                    <div className="admin-table-title">
                      <CreditCard size={16} aria-hidden="true" />
                      <strong>Stripe subscriptions</strong>
                      <span className="admin-count">
                        {subscriptions.length}
                      </span>
                    </div>
                  </div>
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th className="admin-th-server">Discord user</th>
                          <th>Tier</th>
                          <th>Status</th>
                          <th>Stripe customer</th>
                          <th>Subscription ID</th>
                          <th>Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptions.length === 0 && (
                          <tr>
                            <td colSpan={6} className="admin-empty-row">
                              No subscriptions yet
                            </td>
                          </tr>
                        )}
                        {subscriptions.map((sub) => (
                          <tr key={sub.discord_user_id} className="admin-row">
                            <td className="admin-td-server">
                              <code className="admin-server-id">
                                {sub.discord_user_id}
                              </code>
                            </td>
                            <td>
                              <span
                                className={`status-pill ${sub.tier === "chronicle" ? "status-good" : sub.tier === "index" ? "admin-tier-index" : "status-muted"}`}
                              >
                                {sub.tier}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`status-pill ${sub.status === "active" ? "status-good" : "status-muted"}`}
                              >
                                {sub.status}
                              </span>
                            </td>
                            <td>
                              {sub.stripe_customer_id ? (
                                <a
                                  href={`https://dashboard.stripe.com/customers/${sub.stripe_customer_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="admin-stripe-link"
                                >
                                  <code>
                                    {sub.stripe_customer_id.slice(0, 18)}…
                                  </code>
                                </a>
                              ) : (
                                <span className="admin-td-zero">—</span>
                              )}
                            </td>
                            <td>
                              {sub.stripe_subscription_id ? (
                                <a
                                  href={`https://dashboard.stripe.com/subscriptions/${sub.stripe_subscription_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="admin-stripe-link"
                                >
                                  <code>
                                    {sub.stripe_subscription_id.slice(0, 18)}…
                                  </code>
                                </a>
                              ) : (
                                <span className="admin-td-zero">—</span>
                              )}
                            </td>
                            <td className="admin-td-date">
                              {formatDate(sub.updated_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="admin-table-section">
                  <div className="admin-table-header">
                    <div className="admin-table-title">
                      <Gift size={16} aria-hidden="true" />
                      <strong>Gifted subscriptions</strong>
                      <span className="admin-count">{gifts.length}</span>
                    </div>
                  </div>
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th className="admin-th-server">Discord user</th>
                          <th>Tier</th>
                          <th>Status</th>
                          <th>Expires</th>
                          <th>Granted by</th>
                          <th>Granted</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gifts.length === 0 && (
                          <tr>
                            <td colSpan={7} className="admin-empty-row">
                              No gifts granted
                            </td>
                          </tr>
                        )}
                        {gifts.map((gift) => (
                          <tr key={gift.id} className="admin-row">
                            <td className="admin-td-server">
                              <code className="admin-server-id">
                                {gift.discord_user_id}
                              </code>
                            </td>
                            <td>
                              <span
                                className={`status-pill ${gift.tier === "chronicle" ? "status-good" : "admin-tier-index"}`}
                              >
                                {gift.tier}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`status-pill ${gift.active ? "status-good" : "status-muted"}`}
                              >
                                {gift.active ? "active" : "expired"}
                              </span>
                            </td>
                            <td className="admin-td-date">
                              {formatDate(gift.expires_at)}
                            </td>
                            <td>
                              <code className="admin-server-id">
                                {gift.granted_by}
                              </code>
                            </td>
                            <td className="admin-td-date">
                              {formatDate(gift.granted_at)}
                            </td>
                            <td>
                              {gift.note ?? (
                                <span className="admin-td-zero">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            )}
            {adminTab === "usage" && (
              <AdminUsagePanel
                data={usageData}
                loading={usageLoading}
                error={usageError}
                window={usageWindow}
                lastRefreshed={lastRefreshed}
                onWindowChange={setUsageWindow}
                onRefresh={() => void loadUsageData(usageWindow)}
              />
            )}
          </>
        )}
      </div>
    </main>
  );
}

function ServerManagementPage({
  me,
  loading,
  meLoadFailed,
  onNavigate,
  onManageServer,
  onRelogin,
}: {
  me: MeResponse | null;
  loading: boolean;
  meLoadFailed: boolean;
  onNavigate: (page: PageId) => void;
  onManageServer: (serverId: string) => void;
  onRelogin: () => void;
}) {
  const servers = me?.servers ?? [];
  const addableGuilds = me?.addableGuilds ?? [];
  const hasGuildChoices = servers.length > 0 || addableGuilds.length > 0;

  return (
    <main className="marketing-shell">
      <MarketingNav me={me} meLoading={loading} onNavigate={onNavigate} />

      <section className="servers-header">
        <div>
          <p className="eyebrow">Servers</p>
          <h2>Manage Kodama.</h2>
        </div>
        <a className="secondary-button add-bot-button" href={botInviteUrl()}>
          <PlusCircle size={18} aria-hidden="true" />
          Add Kodama
        </a>
      </section>

      {loading && servers.length === 0 ? (
        <EmptyState
          icon={<LoadingDots label="Loading servers" />}
          title="Loading servers"
          body="Checking which Discord servers you can manage."
        />
      ) : meLoadFailed ? (
        <EmptyState
          icon={<ServerIcon size={24} aria-hidden="true" />}
          title="Could not load your servers"
          body="Something went wrong fetching your Discord servers. Try logging in again."
          action={
            <button
              className="primary-button"
              type="button"
              onClick={onRelogin}
            >
              <LogIn size={18} aria-hidden="true" />
              Login with Discord
            </button>
          }
        />
      ) : !hasGuildChoices ? (
        <EmptyState
          icon={<ServerIcon size={24} aria-hidden="true" />}
          title="No manageable servers"
          body="Discord did not return any servers where you have Manage Server or Administrator permission. Try logging in again — this often fixes stale OAuth tokens."
          action={
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <button
                className="primary-button"
                type="button"
                onClick={onRelogin}
              >
                <LogIn size={18} aria-hidden="true" />
                Login again with Discord
              </button>
              <a className="secondary-button" href={botInviteUrl()}>
                <PlusCircle size={18} aria-hidden="true" />
                Add Kodama
              </a>
            </div>
          }
        />
      ) : (
        <section className="server-picker-panel" aria-label="Manage servers">
          <div className="server-picker-bar">
            <ServerIcon size={18} aria-hidden="true" />
            Server picker
          </div>
          <div className="server-picker-body">
            <h3>Select a server to configure</h3>
            <div className="server-orbit">
              {servers.map((server) => (
                <button
                  className="server-bubble server-bubble-installed"
                  key={server.id}
                  type="button"
                  onClick={() => onManageServer(server.id)}
                >
                  <span aria-hidden="true">
                    {server.icon_url ? (
                      <img src={server.icon_url} alt="" aria-hidden="true" />
                    ) : (
                      server.name.slice(0, 2).toUpperCase()
                    )}
                  </span>
                  <strong>{server.name}</strong>
                  <small>
                    {server.has_recap ? "Ready" : "Needs setup"} ·{" "}
                    {server.personality}
                  </small>
                </button>
              ))}
              {addableGuilds.map((guild) => (
                <ServerInviteBubble guild={guild} key={guild.id} />
              ))}
            </div>
          </div>
          <a className="server-picker-add" href={botInviteUrl()}>
            <PlusCircle size={17} aria-hidden="true" />
            Add another server
          </a>
        </section>
      )}
    </main>
  );
}

function ServerInviteBubble({ guild }: { guild: ManageableGuild }) {
  return (
    <a
      className="server-bubble server-bubble-add"
      href={botInviteUrl(guild.id)}
    >
      <span aria-hidden="true">
        {guild.icon_url ? (
          <img src={guild.icon_url} alt="" aria-hidden="true" />
        ) : (
          <PlusCircle size={34} aria-hidden="true" />
        )}
      </span>
      <strong>{guild.name}</strong>
      <small>Add Kodama</small>
    </a>
  );
}

function OverviewPanel({
  detail,
  tier,
  onNavigate,
  onSavePatch,
  saving,
  scanRunning,
  onScan,
  onUpgrade,
}: {
  detail: ServerDetail;
  tier: string;
  onNavigate: (tab: TabId) => void;
  onSavePatch: (patch: SettingsPatch) => Promise<void>;
  saving: boolean;
  scanRunning: boolean;
  onScan: () => void;
  onUpgrade: () => void;
}) {
  const { server, overview } = detail;
  const voiceReady = Boolean(server.voice_profile);
  const recapsReady = Boolean(server.recap_channel_id && server.recap_time);
  const [voiceEditing, setVoiceEditing] = useState(false);
  const [voiceDraft, setVoiceDraft] = useState(server.voice_profile ?? "");
  const [voiceSaving, setVoiceSaving] = useState(false);

  return (
    <div className="tab-panel">
      <section className="summary-grid" aria-label="Overview">
        <StatCard
          label="Message base"
          value={overview.messageCount.toLocaleString()}
          detail={`Last indexed ${formatDate(overview.lastIndexedAt)}`}
          tone="good"
        />
        <StatCard
          label="Memory shelf"
          value={overview.loreCount.toLocaleString()}
          detail="Pinned lore and detected moments"
          tone="good"
        />
        <StatCard
          label="Daily recap"
          value={recapsReady ? "On" : "Off"}
          detail={
            recapsReady
              ? `${server.recap_time} UTC`
              : "Choose a channel and time"
          }
          tone={recapsReady ? "good" : "warn"}
        />
        <StatCard
          label="Guardrail log"
          value={overview.auditCountToday.toLocaleString()}
          detail={`${overview.optedOutCount} members opted out`}
        />
      </section>

      <section className="work-area">
        <div className="panel main-panel">
          <SectionHeader
            eyebrow="Workspace"
            title={`${server.name} ${voiceReady ? "has a working voice model" : "is ready for its first read"}`}
            aside={
              <StatusPill>
                {voiceReady ? "Voice active" : "Learning"}
              </StatusPill>
            }
          />

          <div className="health-grid">
            <div className="health-card">
              <Bot size={18} aria-hidden="true" />
              <div>
                <strong>Bot gateway</strong>
                <span>
                  {overview.botOnline
                    ? "Kodama is installed and reachable in this server"
                    : "Kodama is not reachable in this server"}
                </span>
              </div>
            </div>
            <div className="health-card">
              <Activity size={18} aria-hidden="true" />
              <div>
                <strong>Dashboard API</strong>
                <span>{overview.apiOnline ? "Online" : "Unavailable"}</span>
              </div>
            </div>
            <div className="health-card">
              <CheckCircle2 size={18} aria-hidden="true" />
              <div>
                <strong>Memory index</strong>
                <span>
                  {overview.messageCount > 0
                    ? `${overview.messageCount.toLocaleString()} messages available for lore and context`
                    : "Waiting for server messages"}
                </span>
              </div>
            </div>
            <div className="health-card">
              <History size={18} aria-hidden="true" />
              <div>
                <strong>History scan</strong>
                <span>
                  {overview.scanInProgress
                    ? "Scan is currently running"
                    : "No scan running"}
                </span>
              </div>
            </div>
            <div className="health-card health-card--voice">
              <Sparkles size={18} aria-hidden="true" />
              <div className="health-card-body">
                <div className="health-card-title-row">
                  <strong>Server voice</strong>
                  {!voiceEditing && (
                    <>
                      <button
                        className="icon-button health-card-edit"
                        type="button"
                        aria-label="Edit voice profile"
                        onClick={() => {
                          setVoiceDraft(server.voice_profile ?? "");
                          setVoiceEditing(true);
                        }}
                      >
                        <Pencil size={13} aria-hidden="true" />
                      </button>
                      <button
                        className={`icon-button health-card-edit voice-lock-btn ${server.voice_profile_locked ? "voice-lock-btn--locked" : ""}`}
                        type="button"
                        aria-label={
                          server.voice_profile_locked
                            ? "Unlock voice profile (allow auto-refresh)"
                            : "Lock voice profile (prevent auto-refresh)"
                        }
                        onClick={() =>
                          onSavePatch({
                            voice_profile_locked: !server.voice_profile_locked,
                          })
                        }
                      >
                        <LockKeyhole size={13} aria-hidden="true" />
                      </button>
                    </>
                  )}
                </div>
                {voiceEditing ? (
                  <div className="voice-edit-block">
                    <textarea
                      className="voice-edit-textarea"
                      value={voiceDraft}
                      onChange={(e) => setVoiceDraft(e.target.value)}
                      rows={8}
                      maxLength={6000}
                      placeholder="Describe this server's voice and style..."
                      autoFocus
                    />
                    <div className="voice-edit-actions">
                      <button
                        className="ghost-button"
                        type="button"
                        onClick={() => setVoiceEditing(false)}
                        disabled={voiceSaving}
                      >
                        cancel
                      </button>
                      <button
                        className="primary-button"
                        type="button"
                        disabled={voiceSaving}
                        onClick={async () => {
                          setVoiceSaving(true);
                          await onSavePatch({
                            voice_profile: voiceDraft.trim() || null,
                          });
                          setVoiceSaving(false);
                          setVoiceEditing(false);
                        }}
                      >
                        {voiceSaving ? "saving..." : "save"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <span>
                    {server.voice_profile ??
                      "Kodama will build a style guide from recent messages."}
                  </span>
                )}
              </div>
            </div>
            <div className="health-card">
              <ShieldCheck size={18} aria-hidden="true" />
              <div>
                <strong>Privacy</strong>
                <span>Opt-out, forget, and message deletion are tracked.</span>
              </div>
            </div>
          </div>

          <div className="quick-grid">
            <QuickAction
              icon={<Search size={18} aria-hidden="true" />}
              label="Memory"
              detail="Browse saved lore and detected moments"
              onClick={() => onNavigate("memory")}
            />
            <QuickAction
              icon={<SlidersHorizontal size={18} aria-hidden="true" />}
              label="Controls"
              detail="Tune personality and flame mode"
              onClick={() => onNavigate("settings")}
            />
            <QuickAction
              icon={<CalendarClock size={18} aria-hidden="true" />}
              label="Schedule"
              detail="Set recap channel and time"
              onClick={() => onNavigate("recaps")}
            />
          </div>
        </div>

        <aside className="panel side-panel">
          <SectionHeader
            eyebrow="Controls"
            title="Live settings"
            aside={
              <StatusPill tone={saving ? "warn" : "good"}>Live</StatusPill>
            }
          />
          <div className="setup-list">
            <button
              type="button"
              className="setup-row-btn"
              onClick={() => onNavigate("settings")}
            >
              <span>Personality</span>
              <strong>{server.personality}</strong>
            </button>
            <button
              type="button"
              className="setup-row-btn"
              onClick={() => onNavigate("settings")}
            >
              <span>Reply frequency</span>
              <strong>
                {server.mention_behavior === "silent"
                  ? "@mentions only"
                  : server.mention_behavior === "chatty"
                    ? "Active"
                    : "Conversational"}
              </strong>
            </button>
            <button
              type="button"
              className="setup-row-btn"
              onClick={() => onNavigate("recaps")}
            >
              <span>Recap channel</span>
              <strong>{server.recap_channel_id ?? "Not set"}</strong>
            </button>
            <button
              type="button"
              className="setup-row-btn"
              onClick={() => onNavigate("recaps")}
            >
              <span>Recap time</span>
              <strong>
                {server.recap_time ? `${server.recap_time} UTC` : "Not set"}
              </strong>
            </button>
            <button
              type="button"
              className="setup-row-btn"
              onClick={() => onNavigate("settings")}
            >
              <span>Emoji bank</span>
              <strong>
                {server.emoji_bank.length > 0
                  ? server.emoji_bank.join(" ")
                  : "Not set"}
              </strong>
            </button>
          </div>
          <div className={`setup-scan-row${scanRunning ? " scanning" : ""}`}>
            {meetsRequiredTier(tier, "index") &&
            !meetsRequiredTier(tier, "chronicle") ? (
              <button
                type="button"
                className="secondary-button"
                disabled={scanRunning}
                onClick={onScan}
              >
                {scanRunning ? (
                  <LoadingDots label="Scanning history" />
                ) : (
                  <>
                    <History size={15} aria-hidden="true" />
                    Server scan
                  </>
                )}
              </button>
            ) : !meetsRequiredTier(tier, "index") ? (
              <div
                className="locked-btn-wrap"
                onClick={onUpgrade}
                role="button"
                tabIndex={0}
              >
                <button
                  type="button"
                  className="secondary-button"
                  tabIndex={-1}
                  disabled
                >
                  <History size={15} aria-hidden="true" />
                  Server scan
                </button>
                <span
                  className="locked-btn-icon"
                  aria-label="Upgrade to Index to unlock"
                >
                  <LockKeyhole size={13} aria-hidden="true" />
                </span>
              </div>
            ) : null}
            {meetsRequiredTier(tier, "chronicle") ? (
              <button
                type="button"
                className="secondary-button"
                disabled={scanRunning}
                onClick={onScan}
              >
                {scanRunning ? (
                  <LoadingDots label="Scanning history" />
                ) : (
                  <>
                    <History size={15} aria-hidden="true" />
                    Deep scan
                  </>
                )}
              </button>
            ) : (
              <div
                className="locked-btn-wrap"
                onClick={onUpgrade}
                role="button"
                tabIndex={0}
              >
                <button
                  type="button"
                  className="secondary-button"
                  tabIndex={-1}
                  disabled
                >
                  <History size={15} aria-hidden="true" />
                  Deep scan
                </button>
                <span
                  className="locked-btn-icon"
                  aria-label="Upgrade to Chronicle to unlock"
                >
                  <LockKeyhole size={13} aria-hidden="true" />
                </span>
              </div>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
}

function reportTypeLabel(type: string): string {
  if (type === "inside_joke" || type === "lore") return "Lore";
  if (type === "recap") return "Daily recap";
  return "Update";
}

function LogsPanel({ serverId }: { serverId: string }) {
  const [logs, setLogs] = useState<ScanLog[]>([]);
  const [summary, setSummary] = useState<ScanLogSummary | null>(null);
  const [levelFilter, setLevelFilter] = useState<
    "all" | "info" | "warn" | "error"
  >("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchScanLogs(serverId);
      setLogs(data.logs);
      setSummary(data.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load logs");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [serverId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh logs every 10s so active scans show progress live
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.hidden) return;
      fetchScanLogs(serverId)
        .then((data) => {
          setLogs(data.logs);
          setSummary(data.summary);
        })
        .catch(() => {
          /* silently ignore */
        });
    }, 10_000);
    return () => window.clearInterval(id);
  }, [serverId]);

  function formatLogTime(ts: string): string {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(ts));
  }

  const visibleLogs = useMemo(
    () =>
      levelFilter === "all"
        ? logs
        : logs.filter((entry) => entry.level === levelFilter),
    [levelFilter, logs],
  );
  const counts = useMemo(
    () => ({
      all: logs.length,
      info: logs.filter((entry) => entry.level === "info").length,
      warn: logs.filter((entry) => entry.level === "warn").length,
      error: logs.filter((entry) => entry.level === "error").length,
    }),
    [logs],
  );
  const lastError =
    summary?.lastErrorAt && !Number.isNaN(Date.parse(summary.lastErrorAt))
      ? formatLogTime(summary.lastErrorAt)
      : "None recorded";
  const filters: Array<{
    id: "all" | "info" | "warn" | "error";
    label: string;
    count: number;
  }> = [
    { id: "all", label: "All", count: counts.all },
    { id: "error", label: "Errors", count: counts.error },
    { id: "warn", label: "Warnings", count: counts.warn },
    { id: "info", label: "Info", count: counts.info },
  ];

  function formatMetaValue(value: unknown): string {
    if (value == null) return "null";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return (
    <section className="panel page-panel tab-panel">
      <SectionHeader
        eyebrow="Activity"
        title="Operations logs"
        aside={
          <button
            type="button"
            className="icon-button"
            onClick={() => void load()}
            disabled={loading}
            title="Refresh logs"
            aria-label="Refresh logs"
          >
            <ScrollText size={15} aria-hidden="true" />
          </button>
        }
      />

      {error && <p className="memory-error">{error}</p>}

      <div className="log-observability-grid" aria-label="Log summary">
        <div className="log-summary-card">
          <span>Events in 24h</span>
          <strong>{summary?.total24h ?? 0}</strong>
        </div>
        <div className="log-summary-card log-summary-card-warn">
          <span>Warnings</span>
          <strong>{summary?.warn24h ?? 0}</strong>
        </div>
        <div className="log-summary-card log-summary-card-error">
          <span>Errors</span>
          <strong>{summary?.error24h ?? 0}</strong>
        </div>
        <div className="log-summary-card">
          <span>Last error</span>
          <strong>{lastError}</strong>
        </div>
      </div>

      <div className="log-filter-row" aria-label="Filter logs by severity">
        {filters.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={`log-filter ${
              levelFilter === filter.id ? "log-filter-active" : ""
            }`}
            onClick={() => setLevelFilter(filter.id)}
          >
            {filter.label}
            <span>{filter.count}</span>
          </button>
        ))}
      </div>

      {loading && (
        <div className="empty-state">
          <p>Loading logs…</p>
        </div>
      )}

      {!loading && logs.length === 0 && !error && (
        <div className="empty-state">
          <p>
            No activity logged yet. Scan progress, command failures, and API
            issues will appear here when Kodama has server-scoped events.
          </p>
        </div>
      )}

      {!loading && logs.length > 0 && visibleLogs.length === 0 && !error && (
        <div className="empty-state">
          <p>No logs match this severity filter.</p>
        </div>
      )}

      {!loading && visibleLogs.length > 0 && (
        <ul className="log-list" aria-label="Operations logs">
          {visibleLogs.map((entry) => (
            <li key={entry.id} className={`log-entry log-level-${entry.level}`}>
              <div className="log-row">
                <span
                  className={`log-level-badge log-level-badge-${entry.level}`}
                >
                  {entry.level}
                </span>
                <code className="log-event">{entry.event}</code>
                <span className="log-time">
                  {formatLogTime(entry.created_at)}
                </span>
              </div>
              <p className="log-message">{entry.message}</p>
              {entry.meta && Object.keys(entry.meta).length > 0 && (
                <div className="log-meta">
                  {Object.entries(entry.meta).map(([k, v]) => (
                    <span key={k} className="log-meta-item">
                      <span className="log-meta-key">{k}</span>
                      <span className="log-meta-val">{formatMetaValue(v)}</span>
                    </span>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Module-level: persists across tab switches within a session, resets on page reload.
// Entries added here will no longer show the "New" badge.
const seenMemoryIds = new Set<string>();

const emptyScanLifecycle: ScanLifecycle = {
  state: "idle",
  currentStartedAt: null,
  lastScannedAt: null,
  lastFinishedAt: null,
  failedReason: null,
  estimatedScope: {
    totalChannels: null,
    maxPagesPerChannel: null,
    uncapped: false,
  },
  completedChannels: 0,
  skippedChannels: [],
  skippedChannelCount: 0,
};

function MemoryPanel({
  lore,
  working,
  error,
  tier,
  serverId,
  channels,
  scanLifecycle,
  onAsk,
  onCreate,
  onDelete,
  onUpgrade,
  scanRunning,
  onScan,
  onFullScan,
  onCancelScan,
}: {
  lore: LoreEntry[];
  working: boolean;
  error: string | null;
  tier: string;
  serverId: string;
  channels: GuildChannel[];
  scanLifecycle: ScanLifecycle;
  onAsk: () => void;
  onCreate: (entry: { title: string; summary: string }) => Promise<void>;
  onDelete: (entry: LoreEntry) => Promise<void>;
  onUpgrade: () => void;
  scanRunning: boolean;
  onScan: () => void;
  onFullScan: () => void;
  onCancelScan: () => void;
}) {
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<LoreEntry | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Excluded channels state
  const [exclusions, setExclusions] = useState<ChannelExclusion[]>([]);
  const [exclusionsLoading, setExclusionsLoading] = useState(true);
  const [exclusionError, setExclusionError] = useState<string | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState("");
  const [exclusionReason, setExclusionReason] = useState("");
  const [addingExclusion, setAddingExclusion] = useState(false);
  const [removingExclusionId, setRemovingExclusionId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setExclusionsLoading(true);
    listChannelExclusions(serverId)
      .then(setExclusions)
      .catch(() => setExclusionError("Failed to load exclusions"))
      .finally(() => setExclusionsLoading(false));
  }, [serverId]);

  const excludedChannelIds = useMemo(
    () => new Set(exclusions.map((e) => e.channelId)),
    [exclusions],
  );

  const availableChannels = useMemo(
    () => channels.filter((c) => !excludedChannelIds.has(c.id)),
    [channels, excludedChannelIds],
  );
  const exclusionChannelOptions = useMemo<SelectOption[]>(
    () => [
      {
        value: "",
        label:
          availableChannels.length === 0
            ? "All channels excluded"
            : "Select a channel...",
      },
      ...availableChannels.map((channel) => ({
        value: channel.id,
        label: `#${channel.name}`,
        detail: channel.type === 5 ? "Announcement channel" : "Text channel",
      })),
    ],
    [availableChannels],
  );

  async function handleAddExclusion(): Promise<void> {
    if (!selectedChannelId) return;
    setAddingExclusion(true);
    setExclusionError(null);
    try {
      const entry = await addChannelExclusion(
        serverId,
        selectedChannelId,
        exclusionReason.trim() || undefined,
      );
      setExclusions((prev) => [entry, ...prev]);
      setSelectedChannelId("");
      setExclusionReason("");
    } catch {
      setExclusionError("Failed to add exclusion. Please try again.");
    } finally {
      setAddingExclusion(false);
    }
  }

  async function handleRemoveExclusion(channelId: string): Promise<void> {
    setRemovingExclusionId(channelId);
    setExclusionError(null);
    try {
      await deleteChannelExclusion(serverId, channelId);
      setExclusions((prev) => prev.filter((e) => e.channelId !== channelId));
    } catch {
      setExclusionError("Failed to remove exclusion. Please try again.");
    } finally {
      setRemovingExclusionId(null);
    }
  }

  function resolveChannelName(channelId: string): string {
    const ch = channels.find((c) => c.id === channelId);
    return ch ? `#${ch.name}` : `#${channelId}`;
  }

  function formatScanTime(ts: string | null): string {
    if (!ts) return "Not yet";
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(ts));
  }

  const scanStateLabel = {
    idle: "Idle",
    queued: "Queued",
    running: "Running",
    completed: "Completed",
    cancelled: "Cancelled",
    failed: "Failed",
  }[scanLifecycle.state];
  const scopeText = scanLifecycle.estimatedScope.totalChannels
    ? `${scanLifecycle.estimatedScope.totalChannels} channels${
        scanLifecycle.estimatedScope.uncapped
          ? ", full history"
          : scanLifecycle.estimatedScope.maxPagesPerChannel
            ? `, up to ${scanLifecycle.estimatedScope.maxPagesPerChannel} pages each`
            : ""
      }`
    : scanLifecycle.state === "queued"
      ? "Waiting for channel count"
      : "No recent scan scope";
  const scanProgressText =
    scanLifecycle.state === "running" || scanLifecycle.state === "queued"
      ? `${scanLifecycle.completedChannels} channels completed`
      : scanLifecycle.lastFinishedAt
        ? `Finished ${formatScanTime(scanLifecycle.lastFinishedAt)}`
        : "No completed lifecycle yet";
  const skippedPreview = scanLifecycle.skippedChannels.slice(0, 4);

  const sevenDaysAgo = useMemo(() => Date.now() - 7 * 24 * 60 * 60 * 1000, []);

  // On unmount (tab switch), mark all currently-visible new entries as seen so
  // they don't show "New" if the user comes back to this tab.
  useEffect(() => {
    return () => {
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      for (const entry of lore) {
        if (new Date(entry.created_at).getTime() > cutoff) {
          seenMemoryIds.add(entry.id);
        }
      }
    };
  }, [lore]);

  const filteredLore = useMemo(() => {
    const clean = query.trim().toLowerCase();
    if (!clean) return lore;
    return lore.filter((entry) =>
      `${entry.title} ${entry.summary} ${entry.category ?? ""}`
        .toLowerCase()
        .includes(clean),
    );
  }, [lore, query]);

  async function submitMemory(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextTitle = title.trim();
    const nextSummary = summary.trim();
    if (!nextTitle || !nextSummary) {
      setFormError("Add a title and memory details.");
      return;
    }
    setFormError(null);
    try {
      await onCreate({ title: nextTitle, summary: nextSummary });
      setTitle("");
      setSummary("");
      setAddOpen(false);
    } catch {
      // The parent owns the user-facing API error.
    }
  }

  async function doDeleteMemory(entry: LoreEntry): Promise<void> {
    setConfirmDelete(null);
    setDeletingId(entry.id);
    try {
      await onDelete(entry);
    } catch {
      // The parent owns the user-facing API error.
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <section className="panel page-panel tab-panel">
        <SectionHeader
          eyebrow="Server memory"
          title="Memory"
          aside={
            <div className="memory-header-actions">
              <button
                type="button"
                className="secondary-button memory-action-btn"
                onClick={() => setAddOpen(true)}
              >
                <Pin size={16} aria-hidden="true" />
                Pin memory
              </button>
              {meetsRequiredTier(tier, "chronicle") ? (
                <button
                  type="button"
                  className="secondary-button memory-action-btn"
                  onClick={onAsk}
                >
                  <Search size={16} aria-hidden="true" />
                  Ask Kodama
                </button>
              ) : (
                <button
                  type="button"
                  className="secondary-button memory-action-btn"
                  onClick={onUpgrade}
                >
                  <LockKeyhole size={16} aria-hidden="true" />
                  Ask Kodama
                </button>
              )}
              <div
                className={`memory-scan-group${scanRunning ? " scanning" : ""}`}
              >
                {meetsRequiredTier(tier, "index") &&
                !meetsRequiredTier(tier, "chronicle") ? (
                  <button
                    type="button"
                    className="secondary-button memory-action-btn"
                    disabled={scanRunning}
                    onClick={onScan}
                  >
                    {scanRunning ? (
                      <LoadingDots label="Scanning history" />
                    ) : (
                      <>
                        <History size={16} aria-hidden="true" />
                        Server scan
                      </>
                    )}
                  </button>
                ) : !meetsRequiredTier(tier, "index") ? (
                  <div
                    className="locked-btn-wrap"
                    onClick={onUpgrade}
                    role="button"
                    tabIndex={0}
                  >
                    <button
                      type="button"
                      className="secondary-button memory-action-btn"
                      tabIndex={-1}
                      disabled
                    >
                      <History size={16} aria-hidden="true" />
                      Server scan
                    </button>
                    <span
                      className="locked-btn-icon"
                      aria-label="Upgrade to Index to unlock"
                    >
                      <LockKeyhole size={13} aria-hidden="true" />
                    </span>
                  </div>
                ) : null}
                {meetsRequiredTier(tier, "chronicle") ? (
                  <button
                    type="button"
                    className="secondary-button memory-action-btn"
                    disabled={scanRunning}
                    onClick={onScan}
                  >
                    {scanRunning ? (
                      <LoadingDots label="Scanning history" />
                    ) : (
                      <>
                        <History size={16} aria-hidden="true" />
                        Deep scan
                      </>
                    )}
                  </button>
                ) : (
                  <div
                    className="locked-btn-wrap"
                    onClick={onUpgrade}
                    role="button"
                    tabIndex={0}
                  >
                    <button
                      type="button"
                      className="secondary-button memory-action-btn"
                      tabIndex={-1}
                      disabled
                    >
                      <History size={16} aria-hidden="true" />
                      Deep scan
                    </button>
                    <span
                      className="locked-btn-icon"
                      aria-label="Upgrade to Chronicle to unlock"
                    >
                      <LockKeyhole size={13} aria-hidden="true" />
                    </span>
                  </div>
                )}
                {meetsRequiredTier(tier, "chronicle") ? (
                  <button
                    type="button"
                    className="secondary-button memory-action-btn"
                    disabled={scanRunning}
                    onClick={onFullScan}
                  >
                    {scanRunning ? (
                      <LoadingDots label="Full scanning" />
                    ) : (
                      <>
                        <Zap size={16} aria-hidden="true" />
                        Full scan
                      </>
                    )}
                  </button>
                ) : (
                  <div
                    className="locked-btn-wrap"
                    onClick={onUpgrade}
                    role="button"
                    tabIndex={0}
                  >
                    <button
                      type="button"
                      className="secondary-button memory-action-btn"
                      tabIndex={-1}
                      disabled
                    >
                      <Zap size={16} aria-hidden="true" />
                      Full scan
                    </button>
                    <span
                      className="locked-btn-icon"
                      aria-label="Upgrade to Chronicle to unlock"
                    >
                      <LockKeyhole size={13} aria-hidden="true" />
                    </span>
                  </div>
                )}
              </div>
              {scanRunning && (
                <button
                  type="button"
                  className="secondary-button memory-action-btn memory-cancel-btn"
                  onClick={onCancelScan}
                >
                  <X size={14} aria-hidden="true" />
                  Cancel scan
                </button>
              )}
              <StatusPill tone="muted">
                {lore.length} {lore.length === 1 ? "memory" : "memories"}
              </StatusPill>
            </div>
          }
        />

        <div className={`scan-lifecycle scan-lifecycle-${scanLifecycle.state}`}>
          <div className="scan-lifecycle-main">
            <div>
              <span className="scan-lifecycle-eyebrow">Scan state</span>
              <div className="scan-lifecycle-title-row">
                <strong>{scanStateLabel}</strong>
                {scanLifecycle.currentStartedAt && (
                  <span>
                    Started {formatScanTime(scanLifecycle.currentStartedAt)}
                  </span>
                )}
              </div>
            </div>
            <StatusPill
              tone={
                scanLifecycle.state === "failed"
                  ? "warn"
                  : scanLifecycle.state === "running" ||
                      scanLifecycle.state === "queued"
                    ? "good"
                    : "muted"
              }
            >
              {scanLifecycle.state}
            </StatusPill>
          </div>
          <div className="scan-lifecycle-grid">
            <div>
              <span>Last scanned</span>
              <strong>{formatScanTime(scanLifecycle.lastScannedAt)}</strong>
            </div>
            <div>
              <span>Estimated scope</span>
              <strong>{scopeText}</strong>
            </div>
            <div>
              <span>Progress</span>
              <strong>{scanProgressText}</strong>
            </div>
            <div>
              <span>Skipped channels</span>
              <strong>{scanLifecycle.skippedChannelCount}</strong>
            </div>
          </div>
          {scanLifecycle.failedReason && (
            <p className="scan-lifecycle-failure">
              {scanLifecycle.failedReason}
            </p>
          )}
          {skippedPreview.length > 0 && (
            <div className="scan-skipped-list" aria-label="Skipped channels">
              {skippedPreview.map((channel) => (
                <span
                  key={`${channel.channelId ?? channel.message}-${channel.created_at}`}
                >
                  {channel.channelName
                    ? `#${channel.channelName}`
                    : channel.channelId
                      ? resolveChannelName(channel.channelId)
                      : channel.message}
                </span>
              ))}
              {scanLifecycle.skippedChannelCount > skippedPreview.length && (
                <span>
                  +{scanLifecycle.skippedChannelCount - skippedPreview.length}{" "}
                  more
                </span>
              )}
            </div>
          )}
        </div>

        {error && <p className="memory-error">{error}</p>}

        <label className="search-row">
          <Search size={18} aria-hidden="true" />
          <input
            aria-label="Search memories"
            placeholder="Search jokes, events, people, or phrases"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>

        <div className="memory-list">
          {filteredLore.length === 0 ? (
            <EmptyState
              icon={<Search size={24} aria-hidden="true" />}
              title={lore.length === 0 ? "No memories yet" : "No matches"}
              body={
                lore.length === 0
                  ? "Run a server scan to start building Kodama's memory, or pin one manually."
                  : "Try a different phrase or clear the search."
              }
            />
          ) : (
            filteredLore.map((entry) => {
              const isNew =
                !seenMemoryIds.has(entry.id) &&
                new Date(entry.created_at).getTime() > sevenDaysAgo;
              return (
                <article className="memory-item" key={entry.id}>
                  <div className="memory-icon">
                    {entry.pinned ? (
                      <Pin size={16} aria-hidden="true" />
                    ) : (
                      <MessageCircle size={16} aria-hidden="true" />
                    )}
                  </div>
                  <div>
                    <div className="memory-title-row">
                      <h4>{entry.title}</h4>
                      <div className="memory-actions">
                        <StatusPill
                          tone={
                            entry.pinned ? "good" : isNew ? "info" : "muted"
                          }
                        >
                          {entry.pinned
                            ? "Pinned"
                            : isNew
                              ? "New"
                              : formatCategory(entry.category)}
                        </StatusPill>
                        <button
                          className="icon-button memory-delete"
                          type="button"
                          title="Delete memory"
                          aria-label={`Delete ${entry.title}`}
                          disabled={working || deletingId === entry.id}
                          onClick={() => setConfirmDelete(entry)}
                        >
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                    <p>{entry.summary}</p>
                    <span className="memory-timestamp">
                      {formatDate(entry.created_at)}
                    </span>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </section>

      {addOpen && (
        <div className="modal-scrim" role="presentation">
          <form className="memory-modal" onSubmit={submitMemory}>
            <SectionHeader
              eyebrow="Manual memory"
              title="Pin memory"
              aside={<StatusPill tone="muted">Pinned</StatusPill>}
            />
            <label>
              <span className="field-label">Title</span>
              <input
                maxLength={120}
                placeholder="S1 tournament winner"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label>
              <span className="field-label">Details</span>
              <textarea
                maxLength={1200}
                placeholder="Ryder won the first server tournament after..."
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
              />
            </label>
            {(formError || error) && (
              <small className="save-error">{formError ?? error}</small>
            )}
            <div className="modal-actions">
              <button
                className="secondary-button"
                type="button"
                disabled={working}
                onClick={() => setAddOpen(false)}
              >
                Cancel
              </button>
              <button
                className="primary-button"
                type="submit"
                disabled={working}
              >
                <Pin size={17} aria-hidden="true" />
                {working ? (
                  <LoadingDots label="Saving memory" tone="dark" />
                ) : (
                  "Save memory"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          message={`Delete "${confirmDelete.title}" from Kodama's memory?`}
          confirmLabel="Delete"
          onConfirm={() => void doDeleteMemory(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* ── Excluded channels ─────────────────────────────────────────────────── */}
      <section
        className="panel page-panel exclusions-panel"
        aria-label="Excluded channels"
      >
        <SectionHeader
          eyebrow="Server Memory"
          title="Excluded channels"
          aside={<EyeOff size={18} aria-hidden="true" />}
        />
        <p className="exclusions-help">
          Channels Kodama will not scan or pull memory from. Useful for
          staff-only, off-topic, or NSFW channels.
        </p>

        {exclusionError && <p className="memory-error">{exclusionError}</p>}

        {exclusionsLoading ? (
          <div className="exclusions-loading">
            <LoadingDots />
          </div>
        ) : exclusions.length === 0 ? (
          <p className="exclusions-empty">
            No channels excluded. All channels are included in memory.
          </p>
        ) : (
          <div className="exclusions-list">
            {exclusions.map((ex) => (
              <div className="exclusion-chip" key={ex.channelId}>
                <span className="exclusion-name">
                  {resolveChannelName(ex.channelId)}
                </span>
                {ex.reason && (
                  <span className="exclusion-reason">{ex.reason}</span>
                )}
                <button
                  type="button"
                  className="icon-button exclusion-remove"
                  aria-label={`Remove exclusion for ${resolveChannelName(ex.channelId)}`}
                  disabled={removingExclusionId === ex.channelId}
                  onClick={() => void handleRemoveExclusion(ex.channelId)}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="exclusions-add">
          <div className="exclusions-select">
            <CustomSelect
              label="Select channel to exclude"
              value={selectedChannelId}
              options={exclusionChannelOptions}
              onChange={(next) => {
                if (addingExclusion || availableChannels.length === 0) return;
                setSelectedChannelId(next);
              }}
            />
          </div>
          <input
            className="exclusions-reason-input"
            type="text"
            placeholder="Reason (optional)"
            maxLength={200}
            value={exclusionReason}
            onChange={(e) => setExclusionReason(e.target.value)}
            disabled={addingExclusion}
            aria-label="Reason for exclusion (optional)"
          />
          <button
            type="button"
            className="secondary-button"
            disabled={!selectedChannelId || addingExclusion}
            onClick={() => void handleAddExclusion()}
          >
            {addingExclusion ? (
              <LoadingDots label="Adding exclusion" />
            ) : (
              <>
                <EyeOff size={15} aria-hidden="true" />
                Add exclusion
              </>
            )}
          </button>
        </div>
      </section>
    </>
  );
}

function TypewriterText({ text }: { text: string }) {
  const words = text.split(" ");
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    setVisibleCount(0);
    if (words.length === 0) return;
    let i = 0;
    // Start fast then settle — front-loaded acceleration like real token streaming
    function scheduleNext() {
      if (i >= words.length) return;
      const delay = i < 4 ? 18 : i < 12 ? 28 : 38;
      const timer = window.setTimeout(() => {
        i++;
        setVisibleCount(i);
        scheduleNext();
      }, delay);
      return timer;
    }
    const first = scheduleNext();
    return () => window.clearTimeout(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <span>
      {words.slice(0, visibleCount).join(" ")}
      {visibleCount < words.length && (
        <span className="typewriter-cursor" aria-hidden="true" />
      )}
    </span>
  );
}

function AskKodamaWidget({
  open,
  serverName,
  messages,
  sending,
  error,
  capped,
  onOpen,
  onClose,
  onSend,
  onUpgrade,
}: {
  open: boolean;
  serverName: string;
  messages: AskMessage[];
  sending: boolean;
  error: string | null;
  capped: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSend: (question: string) => Promise<void>;
  onUpgrade: () => void;
}) {
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => inputRef.current?.focus(), 80);
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, open, sending]);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const question = draft.trim();
    if (!question || sending) return;
    setDraft("");
    await onSend(question);
  }

  return (
    <div className={`ask-widget ${open ? "ask-widget-open" : ""}`}>
      {open ? (
        <section className="ask-panel" aria-label="Ask Kodama">
          <header className="ask-header">
            <span className="ask-avatar" aria-hidden="true">
              <img src="/kodama_logo.png" alt="" />
            </span>
            <div>
              <p className="eyebrow">Ask Kodama</p>
              <h3>{serverName}</h3>
            </div>
            <button
              className="icon-button ask-close"
              type="button"
              aria-label="Close Ask Kodama"
              onClick={onClose}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </header>

          <div ref={listRef} className="ask-messages">
            {messages.length === 0 ? (
              <div className="ask-empty">
                <MessageCircle size={22} aria-hidden="true" />
                <strong>Ask about this server</strong>
                <span>Try a person, joke, event, phrase, or saved memory.</span>
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  className={`ask-bubble ask-${message.role}`}
                  key={`${message.role}-${index}`}
                >
                  {message.role === "kodama" &&
                  index === messages.length - 1 &&
                  !sending ? (
                    <TypewriterText text={message.content} />
                  ) : (
                    message.content
                  )}
                </div>
              ))
            )}
            {sending && (
              <div className="ask-bubble ask-kodama ask-loading">
                <LoadingDots />
              </div>
            )}
            {capped && (
              <div className="ask-bubble ask-kodama ask-cap-bubble">
                <span>
                  you&apos;ve used all your Ask Kodama messages for today.
                  resets at midnight UTC.
                </span>
                <button
                  className="primary-button ask-upgrade-btn"
                  type="button"
                  onClick={onUpgrade}
                >
                  upgrade
                </button>
              </div>
            )}
          </div>

          {error && <p className="ask-error">{error}</p>}

          <form className="ask-form" onSubmit={submit}>
            <input
              ref={inputRef}
              aria-label="Ask Kodama a question"
              maxLength={500}
              placeholder={
                capped
                  ? "upgrade to keep asking..."
                  : "Ask about this server..."
              }
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              disabled={capped}
            />
            <button
              className="primary-button ask-send"
              type="submit"
              disabled={sending || draft.trim().length === 0 || capped}
              aria-label="Send question"
            >
              <Send size={17} aria-hidden="true" />
            </button>
          </form>
        </section>
      ) : (
        <button
          className="ask-launcher"
          type="button"
          aria-label="Open Ask Kodama"
          onClick={onOpen}
        >
          <MessageCircle size={22} aria-hidden="true" />
          <span>Ask Kodama</span>
        </button>
      )}
    </div>
  );
}

function LoadingDots({
  label = "Loading",
  tone = "mint",
}: {
  label?: string;
  tone?: "mint" | "dark";
}) {
  return (
    <span className={`loading-dots loading-dots-${tone}`} aria-label={label}>
      <span />
      <span />
      <span />
    </span>
  );
}

function RecapsPanel({
  server,
  saving,
  saveError,
  onSave,
}: {
  server: ServerSummary;
  saving: boolean;
  saveError: string | null;
  onSave: (server: ServerSummary) => void;
}) {
  const ready = Boolean(server.recap_channel_id && server.recap_time);
  const [recapTime, setRecapTime] = useState(server.recap_time ?? "");
  const [recapChannel, setRecapChannel] = useState(
    server.recap_channel_id ?? "",
  );

  // Detect the browser's local timezone for display; shown as a hint to admins
  // so they know recaps fire in UTC and can calculate their local equivalent.
  const [browserTz] = useState<string>(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "UTC";
    }
  });

  useEffect(() => {
    setRecapTime(server.recap_time ?? "");
    setRecapChannel(server.recap_channel_id ?? "");
  }, [server]);

  const hasChanges =
    recapTime !== (server.recap_time ?? "") ||
    recapChannel !== (server.recap_channel_id ?? "");

  return (
    <section className="panel page-panel tab-panel">
      <SectionHeader
        eyebrow="Daily recaps"
        title="Schedule"
        aside={
          <StatusPill tone={ready ? "good" : "warn"}>
            {ready ? "Active" : "Needs setup"}
          </StatusPill>
        }
      />

      <div className="recap-board">
        <div className="recap-main">
          <Clock3 size={26} aria-hidden="true" />
          <span>Daily recap time</span>
          <strong>
            {server.recap_time ? `${server.recap_time} UTC` : "Not scheduled"}
          </strong>
          <p>
            {ready
              ? `Kodama will post each recap in channel ${server.recap_channel_id}.`
              : "Choose a Discord text channel and a UTC time to start daily recaps."}
          </p>
        </div>
        <div className="recap-rules">
          <h4>Included in recaps</h4>
          <ul>
            <li>Big conversations and decisions</li>
            <li>New lore moments and recurring jokes</li>
            <li>Notable member activity, excluding opted-out users</li>
          </ul>
        </div>
      </div>

      <div className="settings-grid" style={{ marginTop: 14 }}>
        <label>
          <span className="field-label">Daily recap time UTC</span>
          <input
            type="time"
            step="60"
            placeholder="21:00"
            value={recapTime}
            onChange={(event) => setRecapTime(event.target.value)}
            onBlur={(event) => {
              // Normalize: strip trailing :SS so we always store HH:MM
              const v = event.target.value.trim();
              if (/^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(v)) {
                setRecapTime(v.slice(0, 5));
              }
            }}
          />
          <small>
            Use 24-hour UTC time, for example 21:00.
            {browserTz && browserTz !== "UTC" && (
              <>
                {" "}
                Your browser timezone is <strong>{browserTz}</strong> — recaps
                fire in UTC, so adjust accordingly.
              </>
            )}
          </small>
        </label>
        <label>
          <span className="field-label">Recap channel ID</span>
          <input
            placeholder="Discord channel ID"
            value={recapChannel}
            onChange={(event) => setRecapChannel(event.target.value)}
          />
          <small>Paste the channel ID where daily recaps should post.</small>
        </label>
      </div>

      <div className="panel-footer">
        {saveError && <small className="save-error">{saveError}</small>}
        <button
          className="primary-button save-button"
          disabled={saving || !hasChanges}
          type="button"
          onClick={() => {
            // Strip :SS if present before sending to API
            const normalizedTime =
              recapTime.trim().replace(/^(\d{2}:\d{2}):\d{2}$/, "$1") || null;
            onSave({
              ...server,
              recap_time: normalizedTime,
              recap_channel_id: recapChannel.trim() || null,
              has_recap: Boolean(normalizedTime && recapChannel.trim()),
            });
          }}
        >
          <Zap size={18} aria-hidden="true" />
          {saving ? <LoadingDots label="Saving" tone="dark" /> : "Save changes"}
        </button>
      </div>
    </section>
  );
}

const BEHAVIOR_TOGGLES: Array<{
  key: keyof ServerSummary;
  label: string;
  description: string;
}> = [
  {
    key: "allow_profanity",
    label: "Allow profanity",
    description: "Kodama can use and echo swear words in replies.",
  },
  {
    key: "allow_roasting",
    label: "Allow roasting members",
    description: "Kodama can roast or flame members when asked.",
  },
  {
    key: "allow_organic_replies",
    label: "Organic replies",
    description: "Kodama can jump into conversations unprompted.",
  },
  {
    key: "allow_emoji_reactions",
    label: "Emoji reactions",
    description: "Kodama reacts to messages with emoji.",
  },
  {
    key: "allow_indexing",
    label: "Message indexing",
    description:
      "Kodama indexes messages into memory. Disable to pause learning.",
  },
  {
    key: "allow_recaps",
    label: "Daily recaps",
    description: "Kodama posts scheduled server recaps.",
  },
];

function PrivacyPanel({
  server,
  audit,
}: {
  server: ServerSummary;
  audit: AuditEvent[];
}) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExport(): Promise<void> {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await exportMyServerData(server.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `kodama-export-${server.id}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const message =
        err instanceof ApiError &&
        err.message &&
        !err.message.startsWith("API ")
          ? err.message
          : "Could not export your data. Try again.";
      setExportError(message);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="tab-panel">
      <section className="panel page-panel">
        <SectionHeader
          eyebrow="Guardrails"
          title="Member control"
          aside={<StatusPill>Protected</StatusPill>}
        />

        <div className="privacy-grid">
          <article className="privacy-card">
            <LockKeyhole size={20} aria-hidden="true" />
            <strong>Opt out</strong>
            <p>
              Members can remove themselves from future memory and summaries.
            </p>
            <code>/optout</code>
          </article>
          <article className="privacy-card">
            <Trash2 size={20} aria-hidden="true" />
            <strong>Forget a message</strong>
            <p>
              Members can remove their own message. Mods can remove any indexed
              message.
            </p>
            <code>/forget message_link</code>
          </article>
          <article className="privacy-card">
            <ShieldCheck size={20} aria-hidden="true" />
            <strong>Export data</strong>
            <p>Members can download their indexed data for this server.</p>
            <button
              type="button"
              className="secondary-button"
              onClick={() => void handleExport()}
              disabled={exporting}
            >
              <Download size={15} aria-hidden="true" />
              {exporting ? "Exporting..." : "Export my data"}
            </button>
          </article>
        </div>
        {exportError && <p className="save-error">{exportError}</p>}
        <p className="privacy-note">
          You can also use <code>/export</code> in Discord. Deleted Discord
          messages are removed from Kodama automatically.
        </p>
      </section>

      <section className="panel page-panel activity-panel">
        <SectionHeader eyebrow="Audit" title="Recent activity" />
        <div className="activity-list">
          {audit.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck size={24} aria-hidden="true" />}
              title="No recent privacy events"
              body="Opt-outs, forget requests, deleted messages, and setting changes will appear here."
            />
          ) : (
            audit.map((event) => (
              <article className="activity-item" key={event.id}>
                <ShieldCheck size={18} aria-hidden="true" />
                <div>
                  <h4>{formatAction(event.action)}</h4>
                  <p>
                    {event.target ?? "Server"} by{" "}
                    {event.actor_discord_id ?? "system"}
                  </p>
                </div>
                <span>{formatDate(event.created_at)}</span>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function SettingsPanel({
  server,
  saving,
  saveError,
  onSave,
  onSavePatch,
  onUpgrade,
}: {
  server: ServerSummary;
  saving: boolean;
  saveError: string | null;
  onSave: (server: ServerSummary) => void;
  onSavePatch: (patch: SettingsPatch) => void;
  onUpgrade?: () => void;
}) {
  const personalityOptions: SelectOption[] = [
    {
      value: "mirror",
      label: "Mirror the server",
      detail: "Default, follows the learned server voice",
    },
    { value: "chill", label: "Chill", detail: "Low-key and concise" },
    { value: "hype", label: "Hype", detail: "Energetic without going corny" },
    {
      value: "unhinged",
      label: "Unhinged",
      detail: "Weirder and sharper, still bounded",
    },
    {
      value: "historian",
      label: "Historian",
      detail: "More factual and memory-forward",
    },
    {
      value: "custom",
      label: "Custom",
      detail: "Write your own personality direction",
    },
  ];
  const [personality, setPersonality] = useState(server.personality);
  const [customPersonality, setCustomPersonality] = useState(
    server.custom_personality ?? "",
  );
  const [mentionBehaviorSetting, setMentionBehaviorSetting] = useState(
    server.mention_behavior,
  );
  const [emojiBank, setEmojiBank] = useState(server.emoji_bank.join(" "));
  const [lockEmojiBank, setLockEmojiBank] = useState(server.lock_emoji_bank);
  const [filterMode, setFilterMode] = useState<
    "all" | "whitelist" | "blacklist"
  >((server.channel_filter_mode as "all" | "whitelist" | "blacklist") ?? "all");
  const [whitelistIds, setWhitelistIds] = useState<string[]>(
    (server.channel_filter_mode === "whitelist"
      ? server.channel_filter_ids
      : null) ?? [],
  );
  const [blacklistIds, setBlacklistIds] = useState<string[]>(
    (server.channel_filter_mode === "blacklist"
      ? server.channel_filter_ids
      : null) ?? [],
  );
  const [systemPrompt, setSystemPrompt] = useState(server.system_prompt ?? "");
  const [channels, setChannels] = useState<GuildChannel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelSearch, setChannelSearch] = useState("");

  const [toggles, setToggles] = useState<Partial<Record<string, boolean>>>(
    Object.fromEntries(
      BEHAVIOR_TOGGLES.map((t) => [t.key, (server[t.key] as boolean) ?? true]),
    ),
  );

  useEffect(() => {
    setPersonality(server.personality);
    setCustomPersonality(server.custom_personality ?? "");
    setMentionBehaviorSetting(server.mention_behavior);
    setEmojiBank(server.emoji_bank.join(" "));
    setLockEmojiBank(server.lock_emoji_bank);
    setFilterMode(
      (server.channel_filter_mode as "all" | "whitelist" | "blacklist") ??
        "all",
    );
    setWhitelistIds(
      (server.channel_filter_mode === "whitelist"
        ? server.channel_filter_ids
        : null) ?? [],
    );
    setBlacklistIds(
      (server.channel_filter_mode === "blacklist"
        ? server.channel_filter_ids
        : null) ?? [],
    );
    setSystemPrompt(server.system_prompt ?? "");
  }, [server]);

  // Fetch channels when filter mode changes away from "all"
  // Fetch channels on mount so they're ready when the user switches modes
  useEffect(() => {
    setChannelsLoading(true);
    fetchServerChannels(server.id)
      .then(setChannels)
      .catch(() => setChannels([]))
      .finally(() => setChannelsLoading(false));
  }, [server.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setToggles(
      Object.fromEntries(
        BEHAVIOR_TOGGLES.map((t) => [
          t.key,
          (server[t.key] as boolean) ?? true,
        ]),
      ),
    );
  }, [server]);

  function handleToggle(key: string, value: boolean) {
    const next = { ...toggles, [key]: value };
    setToggles(next);
    onSavePatch({ [key]: value } as SettingsPatch);
  }

  const hasChanges =
    personality !== server.personality ||
    customPersonality !== (server.custom_personality ?? "") ||
    mentionBehaviorSetting !== server.mention_behavior ||
    emojiBank.trim() !== server.emoji_bank.join(" ") ||
    lockEmojiBank !== server.lock_emoji_bank ||
    filterMode !== (server.channel_filter_mode ?? "all") ||
    JSON.stringify(
      [...(filterMode === "whitelist" ? whitelistIds : blacklistIds)].sort(),
    ) !== JSON.stringify([...(server.channel_filter_ids ?? [])].sort()) ||
    systemPrompt !== (server.system_prompt ?? "");
  const customPersonalityRequired =
    personality === "custom" && customPersonality.trim().length < 8;

  return (
    <section className="panel page-panel tab-panel">
      <SectionHeader
        eyebrow="Controls"
        title="Personality & Schedule"
        aside={
          <StatusPill tone={hasChanges ? "warn" : "muted"}>
            {hasChanges ? "Unsaved" : "Saved"}
          </StatusPill>
        }
      />

      <div className="settings-grid">
        <label>
          <span className="field-label">Personality</span>
          <CustomSelect
            label="Personality"
            value={personality}
            options={personalityOptions}
            onChange={setPersonality}
          />
          <small>Mirror is the default server voice mode.</small>
        </label>

        <div className="reply-freq-field">
          <span className="field-label">Reply frequency</span>
          <div
            className="reply-freq-picker"
            role="radiogroup"
            aria-label="Reply frequency"
          >
            {(
              [
                {
                  value: "silent",
                  label: "@mentions only",
                  description:
                    "Only replies when directly @tagged. No follow-ups, no random replies.",
                },
                {
                  value: "contextual",
                  label: "Conversational",
                  description:
                    "Replies to @mentions and follows threads it's been pulled into. Won't jump in unprompted.",
                },
                {
                  value: "chatty",
                  label: "Active",
                  description:
                    "Everything above, plus occasionally jumps into conversations on its own when the topic feels relevant.",
                },
              ] as const
            ).map(({ value, label, description }) => {
              const active =
                mentionBehaviorSetting === value ||
                (value === "contextual" &&
                  (mentionBehaviorSetting === "concise" ||
                    mentionBehaviorSetting === "contextual")) ||
                (value === "chatty" && mentionBehaviorSetting === "chatty") ||
                (value === "silent" && mentionBehaviorSetting === "silent");
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`reply-freq-option${active ? " reply-freq-active" : ""}`}
                  onClick={() => setMentionBehaviorSetting(value)}
                >
                  <strong>{label}</strong>
                  <span>{description}</span>
                </button>
              );
            })}
          </div>
        </div>

        <label className="wide-field custom-personality-field">
          <span className="field-label">Custom system prompt</span>
          <textarea
            maxLength={4000}
            rows={8}
            placeholder={`Optional — always applied on top of the personality mode above.\n\nExamples:\n• "You are a customer support bot for AcmeCo. The store sells sneakers, hats, and hoodies. For order issues, tell users to email support@acmeco.com."\n• "If somebody asks for a free dupe or how to dupe, ping @SupportRole with no further information."\n• "This is a gaming server. Hype members up around tournaments every Friday."`}
            value={systemPrompt}
            onChange={(event) => setSystemPrompt(event.target.value)}
          />
          <small>
            Server-specific instructions always injected into the bot's context.
            Use it to define the bot's role, describe your server, or set
            response rules. Works alongside any personality mode.
            {systemPrompt.length > 0 && (
              <span style={{ float: "right", opacity: 0.6 }}>
                {systemPrompt.length}/4000
              </span>
            )}
          </small>
        </label>

        {personality === "custom" && (
          <label className="wide-field custom-personality-field">
            <span className="field-label">Custom personality description</span>
            <textarea
              maxLength={2000}
              rows={5}
              placeholder='Describe the voice and tone for "Custom" mode. e.g. "dry and observant, lightly sarcastic, never too formal, uses lowercase"'
              value={customPersonality}
              onChange={(event) => setCustomPersonality(event.target.value)}
            />
            <small>
              Defines how Kodama writes when personality is set to Custom.
              Overrides the learned server voice.
              {customPersonality.length > 0 && (
                <span style={{ float: "right", opacity: 0.6 }}>
                  {customPersonality.length}/2000
                </span>
              )}
            </small>
          </label>
        )}

        <div className="wide-field emoji-bank-field">
          <span className="field-label">Emoji bank</span>
          <div className="emoji-bank-row">
            <label className="emoji-learn-toggle">
              <div className="emoji-learn-toggle-text">
                <strong>{lockEmojiBank ? "Fixed" : "Learning"}</strong>
                <span>
                  {lockEmojiBank
                    ? "Only uses emojis listed"
                    : "Picks up new emojis from server"}
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!lockEmojiBank}
                className={`toggle-switch${!lockEmojiBank ? " toggle-on" : ""}`}
                onClick={() => setLockEmojiBank((v) => !v)}
              >
                <span className="toggle-thumb" />
              </button>
            </label>
            <div className="emoji-input-wrap">
              <input
                key={server.id + "_emoji_" + server.emoji_bank.join("")}
                placeholder="No emoji bank set"
                defaultValue={emojiBank}
                onChange={(event) => setEmojiBank(event.target.value)}
              />
              <small>
                Paste emoji separated by spaces. Kodama draws from these in
                reactions and replies.
              </small>
            </div>
          </div>
        </div>

        <div className="wide-field behavior-toggles">
          <h4>Behavior controls</h4>
          {BEHAVIOR_TOGGLES.map((t) => (
            <label key={t.key} className="toggle-row">
              <div className="toggle-info">
                <strong>{t.label}</strong>
                <span>{t.description}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={toggles[t.key] ?? true}
                disabled={saving}
                className={`toggle-switch${(toggles[t.key] ?? true) ? " toggle-on" : ""}`}
                onClick={() =>
                  handleToggle(t.key as string, !(toggles[t.key] ?? true))
                }
              >
                <span className="toggle-thumb" />
              </button>
            </label>
          ))}

          {/* Vision (image/GIF understanding) is a per-user Chronicle feature.
               It is not a server-level toggle — each Chronicle member gets it
               automatically wherever they post. No dashboard toggle needed. */}
        </div>

        <div className="wide-field channel-filter-section">
          <h4>Channel filter</h4>
          <p className="channel-filter-desc">
            Control which channels Kodama is active in.
          </p>
          <div className="channel-filter-mode-picker">
            {(
              [
                {
                  value: "all",
                  label: "All channels",
                  desc: "Kodama is active in every channel",
                },
                {
                  value: "whitelist",
                  label: "Whitelist",
                  desc: "Kodama only responds in the selected channels",
                },
                {
                  value: "blacklist",
                  label: "Blacklist",
                  desc: "Kodama stays silent in the selected channels",
                },
              ] as {
                value: "all" | "whitelist" | "blacklist";
                label: string;
                desc: string;
              }[]
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`channel-filter-mode-opt${filterMode === opt.value ? " channel-filter-mode-active" : ""}`}
                onClick={() => setFilterMode(opt.value)}
                disabled={saving}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="channel-filter-mode-hint">
            {filterMode === "all" && "Kodama is active in every channel."}
            {filterMode === "whitelist" &&
              "Kodama only responds in the selected channels."}
            {filterMode === "blacklist" &&
              "Kodama stays silent in the selected channels."}
          </p>

          {filterMode !== "all" && (
            <div className="channel-list-wrap">
              <div className="channel-search-row">
                <input
                  className="channel-search-input"
                  type="text"
                  placeholder="Search channels…"
                  value={channelSearch}
                  onChange={(e) => setChannelSearch(e.target.value)}
                  disabled={channelsLoading || saving}
                />
              </div>
              <div className="channel-list">
                {channelsLoading ? (
                  <p className="channel-list-loading">Loading channels…</p>
                ) : channels.length === 0 ? (
                  <p className="channel-list-loading">
                    No text channels found.
                  </p>
                ) : (
                  (() => {
                    const q = channelSearch.toLowerCase().trim();
                    const visible = channels.filter(
                      (ch) => !q || ch.name.toLowerCase().includes(q),
                    );
                    if (visible.length === 0)
                      return (
                        <p className="channel-list-loading">
                          No channels match.
                        </p>
                      );
                    const activeIds =
                      filterMode === "whitelist" ? whitelistIds : blacklistIds;
                    const setActiveIds =
                      filterMode === "whitelist"
                        ? setWhitelistIds
                        : setBlacklistIds;
                    // checked channels float to top
                    const sorted = [
                      ...visible.filter((ch) => activeIds.includes(ch.id)),
                      ...visible.filter((ch) => !activeIds.includes(ch.id)),
                    ];
                    return sorted.map((ch) => {
                      const checked = activeIds.includes(ch.id);
                      return (
                        <div
                          key={ch.id}
                          className={`channel-list-item${checked ? " channel-list-item-checked" : ""}`}
                          role="checkbox"
                          aria-checked={checked}
                          tabIndex={0}
                          onClick={() => {
                            if (saving) return;
                            setActiveIds((prev) =>
                              checked
                                ? prev.filter((id) => id !== ch.id)
                                : [...prev, ch.id],
                            );
                          }}
                          onKeyDown={(e) => {
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                              if (saving) return;
                              setActiveIds((prev) =>
                                checked
                                  ? prev.filter((id) => id !== ch.id)
                                  : [...prev, ch.id],
                              );
                            }
                          }}
                        >
                          <span
                            className="channel-list-check"
                            aria-hidden="true"
                          >
                            {checked ? "✓" : ""}
                          </span>
                          <span className="channel-list-name"># {ch.name}</span>
                        </div>
                      );
                    });
                  })()
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="panel-footer">
        {saveError && <small className="save-error">{saveError}</small>}
        <button
          className="primary-button save-button"
          disabled={saving || !hasChanges || customPersonalityRequired}
          type="button"
          onClick={() =>
            onSave({
              ...server,
              personality,
              custom_personality: customPersonality.trim() || null,
              mention_behavior: mentionBehaviorSetting,
              emoji_bank: emojiBank.trim()
                ? emojiBank.trim().split(/\s+/)
                : server.emoji_bank,
              lock_emoji_bank: lockEmojiBank,
              channel_filter_mode: filterMode,
              channel_filter_ids:
                filterMode === "whitelist"
                  ? whitelistIds
                  : filterMode === "blacklist"
                    ? blacklistIds
                    : [],
              system_prompt: systemPrompt.trim() || null,
            })
          }
        >
          <Zap size={18} aria-hidden="true" />
          {saving ? (
            <LoadingDots label="Saving changes" tone="dark" />
          ) : (
            "Save changes"
          )}
        </button>
      </div>
    </section>
  );
}

function BotProfilePanel({
  server,
  userTier,
  saving,
  saveError,
  onSave,
  onUpgrade,
}: {
  server: ServerSummary;
  userTier: string;
  saving: boolean;
  saveError: string | null;
  onSave: (server: ServerSummary) => void;
  onUpgrade: () => void;
}) {
  const [displayName, setDisplayName] = useState(server.bot_display_name ?? "");
  const [avatarUrl, setAvatarUrl] = useState(server.bot_avatar_url ?? "");
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [description, setDescription] = useState(server.bot_description ?? "");
  const [banner, setBanner] = useState(server.bot_banner ?? "");
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [bannerColor, setBannerColor] = useState(
    server.bot_banner?.startsWith("#") ? server.bot_banner : "#2fd889",
  );
  const [bannerMode, setBannerMode] = useState<"image" | "color">(
    server.bot_banner?.startsWith("#") ? "color" : "image",
  );
  const [showColorPicker, setShowColorPicker] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayName(server.bot_display_name ?? "");
    setAvatarUrl(server.bot_avatar_url ?? "");
    setAvatarError(null);
    setDescription(server.bot_description ?? "");
    setBanner(server.bot_banner ?? "");
    setBannerError(null);
    setBannerColor(
      server.bot_banner?.startsWith("#") ? server.bot_banner : "#2fd889",
    );
    setBannerMode(server.bot_banner?.startsWith("#") ? "color" : "image");
  }, [server]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(e.target as Node)
      ) {
        setShowColorPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const nextDisplayName = displayName.trim() || null;
  const nextAvatarUrl = avatarUrl.trim() || null;
  const previewAvatarUrl = nextAvatarUrl || defaultBotAvatarUrl;
  const nextDescription = description.trim() || null;
  const nextBanner =
    bannerMode === "color" ? bannerColor : banner.trim() || null;
  const previewBanner =
    bannerMode === "color" ? bannerColor : banner.trim() || defaultBannerUrl;

  const hasChanges =
    (nextDisplayName ?? "") !== (server.bot_display_name ?? "") ||
    (nextAvatarUrl ?? "") !== (server.bot_avatar_url ?? "") ||
    (nextDescription ?? "") !== (server.bot_description ?? "") ||
    (nextBanner ?? "") !== (server.bot_banner ?? "");

  function handleAvatarUpload(file: File | undefined): void {
    if (!file) return;
    if (file.size > maxAvatarBytes) {
      setAvatarError("Profile images must be 1 MB or smaller.");
      return;
    }
    setAvatarError(null);
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") setAvatarUrl(reader.result);
    });
    reader.readAsDataURL(file);
  }

  function handleBannerUpload(file: File | undefined): void {
    if (!file) return;
    if (file.size > maxAvatarBytes * 5) {
      setBannerError("Banner must be 5 MB or smaller.");
      return;
    }
    setBannerError(null);
    setBannerMode("image");
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") setBanner(reader.result);
    });
    reader.readAsDataURL(file);
  }

  return (
    <section className="panel page-panel tab-panel">
      <SectionHeader
        eyebrow="Bot profile"
        title="How Kodama appears here"
        aside={
          <StatusPill tone={hasChanges ? "warn" : "muted"}>
            {hasChanges ? "Unsaved" : "Saved"}
          </StatusPill>
        }
      />

      <div className="profile-config">
        <div className="bot-preview-card" aria-label="Bot profile preview">
          <span className="bot-preview-avatar">
            <img src={previewAvatarUrl} alt="" aria-hidden="true" />
          </span>
          <div>
            <p className="eyebrow">{server.name}</p>
            <h4>{nextDisplayName || "Kodama"}</h4>
            <span>{nextDescription || "No description set"}</span>
          </div>
        </div>

        <div className="settings-grid bot-settings-grid">
          <label>
            <span className="field-label">Display name</span>
            <input
              maxLength={32}
              placeholder="Kodama"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
            <small>
              Leave empty to use default. Syncs to this server's nickname.
            </small>
          </label>

          <label>
            <span className="field-label">Profile image</span>
            <input
              placeholder="https://... or upload below"
              value={avatarUrl.startsWith("data:") ? "" : avatarUrl}
              onChange={(event) => {
                setAvatarError(null);
                setAvatarUrl(event.target.value);
              }}
            />
            <div className="upload-row">
              <button
                className="secondary-button"
                type="button"
                onClick={() => avatarInputRef.current?.click()}
              >
                <Upload size={16} aria-hidden="true" /> Upload image
              </button>
              <input
                ref={avatarInputRef}
                accept="image/png,image/jpeg,image/webp"
                className="file-input"
                type="file"
                onChange={(event) => {
                  handleAvatarUpload(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              <small>PNG, JPG, or WebP. Max 1 MB.</small>
            </div>
            {avatarError && (
              <small className="field-error">{avatarError}</small>
            )}
          </label>

          <LockedSection
            requiredTier="chronicle"
            currentTier={userTier}
            onUpgrade={onUpgrade}
          >
            <label>
              <span className="field-label">Description</span>
              <textarea
                maxLength={350}
                rows={4}
                placeholder="Leave empty for default Kodama description."
                value={description}
                disabled={!meetsRequiredTier(userTier, "chronicle")}
                onChange={(event) => setDescription(event.target.value)}
              />
              <small>{description.length}/350 characters</small>
            </label>
          </LockedSection>
        </div>
      </div>

      {/* Banner section */}
      <div className="banner-section">
        <h4 className="banner-section-label">Server banner</h4>
        <div
          className="banner-preview"
          style={{
            background:
              bannerMode === "color" ? (previewBanner ?? "#2fd889") : undefined,
            backgroundImage:
              bannerMode === "image" && previewBanner
                ? `url(${previewBanner})`
                : undefined,
          }}
          aria-label="Banner preview"
        />
        <div className="banner-controls">
          <button
            className="secondary-button"
            type="button"
            onClick={() => bannerInputRef.current?.click()}
          >
            <Upload size={16} aria-hidden="true" /> Replace image
          </button>
          <input
            ref={bannerInputRef}
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="file-input"
            type="file"
            onChange={(event) => {
              handleBannerUpload(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <div className="color-picker-wrap" ref={colorPickerRef}>
            <button
              className="secondary-button color-swatch-btn"
              type="button"
              onClick={() => setShowColorPicker((v) => !v)}
            >
              <span
                className="color-swatch"
                style={{ background: bannerColor }}
              />
              Set color
            </button>
            {showColorPicker && (
              <div className="color-picker-popup">
                <p className="color-picker-label">Pick a color</p>
                <input
                  type="color"
                  value={bannerColor}
                  className="color-picker-input"
                  onChange={(e) => {
                    setBannerColor(e.target.value);
                    setBannerMode("color");
                  }}
                />
                <p className="color-picker-hex">{bannerColor}</p>
              </div>
            )}
          </div>
          {bannerMode === "image" && banner && (
            <button
              className="secondary-button"
              type="button"
              onClick={() => {
                setBanner("");
                setBannerMode("color");
              }}
            >
              Remove image
            </button>
          )}
        </div>
        {bannerError && <small className="field-error">{bannerError}</small>}
      </div>

      <div className="panel-footer">
        {saveError && <small className="save-error">{saveError}</small>}
        <button
          className="primary-button save-button"
          disabled={saving || !hasChanges}
          type="button"
          onClick={() =>
            onSave({
              ...server,
              bot_display_name: nextDisplayName,
              bot_avatar_url: nextAvatarUrl || null,
              bot_description: nextDescription,
              bot_banner: nextBanner,
            })
          }
        >
          <Bot size={18} aria-hidden="true" />
          {saving ? (
            <LoadingDots label="Saving profile" tone="dark" />
          ) : (
            "Save profile"
          )}
        </button>
      </div>
    </section>
  );
}

const FREE_FEATURES = [
  "Kodama interactions",
  "Privacy controls (/optout, /forget)",
  "Dashboard overview",
  "Behavior controls & guardrails",
  "Ask Kodama: 5 messages/day",
];
const INDEX_FEATURES = [
  "Server scan (recent history)",
  "Daily recaps + /recap command",
  "/whois member profiles",
  "Organic replies, emoji reactions, flame mode",
  "Image & GIF understanding — share screenshots, memes, and GIFs and Kodama will see and react to them.",
  "All personality modes + custom",
  "Emoji bank & channel filtering",
  "Bot profile: display name, avatar, status, banner",
  "Ask Kodama: 20 messages/day",
];
const CHRONICLE_FEATURES = [
  "Full history scan",
  "No 20/day Kodama interaction cap for anyone in servers you own or manage",
  "Bot profile description",
  "Inside joke detection + reports feed",
  "Ask Kodama: unlimited",
  "Voice profile learning",
];

function TierCard({
  id,
  name,
  priceNum,
  tagline,
  features,
  inheritsFrom,
  badge,
  accent,
  current,
  userTier,
  pendingTier,
  onUpgrade,
  onManageBilling,
}: {
  id: "free" | "index" | "chronicle";
  name: string;
  priceNum: number;
  tagline: string;
  features: string[];
  inheritsFrom?: string;
  badge?: string;
  accent?: "mint" | "gold" | "cyan";
  current?: boolean;
  userTier: string;
  pendingTier: "index" | "chronicle" | "portal" | null;
  onUpgrade: (tier: "index" | "chronicle") => void;
  onManageBilling: () => void;
}) {
  const tierRank = (t: string) =>
    t === "chronicle" ? 2 : t === "index" ? 1 : 0;
  const userRank = tierRank(userTier);
  const cardRank = tierRank(id);
  const canUpgrade = !current && id !== "free" && cardRank > userRank;
  const isPending = pendingTier === id || pendingTier === "portal";
  return (
    <div
      className={[
        "tier-card",
        accent ? `tier-card-${accent}` : "",
        current ? "tier-card-current" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {badge && !current && (
        <div className={`tier-badge tier-badge-${accent ?? "mint"}`}>
          {badge}
        </div>
      )}
      {current && (
        <div className="tier-badge tier-badge-current">Current plan</div>
      )}

      <div className="tier-card-top">
        <strong className="tier-name">{name}</strong>
        <div className="tier-price-block">
          {priceNum === 0 ? (
            <span className="tier-price-num">Free</span>
          ) : (
            <>
              <span className="tier-price-num">${priceNum}</span>
              <span className="tier-price-period">/ mo</span>
            </>
          )}
        </div>
        <p className="tier-tagline">{tagline}</p>
      </div>

      <ul className="tier-feature-list">
        {inheritsFrom && (
          <li className="tier-inherits">
            <Check size={14} aria-hidden="true" />
            Everything in {inheritsFrom}
          </li>
        )}
        {inheritsFrom && features.length > 0 && (
          <li className="tier-feature-divider" aria-hidden="true" />
        )}
        {features.map((f) => (
          <li key={f}>
            <Check size={14} aria-hidden="true" />
            {f}
          </li>
        ))}
      </ul>

      <div className="tier-card-cta">
        {canUpgrade && (
          <button
            type="button"
            className={
              accent === "mint"
                ? "primary-button"
                : accent === "gold"
                  ? "tier-cta-gold"
                  : "secondary-button"
            }
            disabled={Boolean(pendingTier)}
            onClick={() => onUpgrade(id as "index" | "chronicle")}
          >
            {isPending ? (
              <LoadingDots label="Opening checkout" tone="dark" />
            ) : (
              `Upgrade to ${name}`
            )}
          </button>
        )}
        {current && (
          <>
            <p className="tier-current-label">You&rsquo;re on this plan</p>
            {id !== "free" && (
              <button
                type="button"
                className="secondary-button"
                disabled={Boolean(pendingTier)}
                onClick={onManageBilling}
              >
                {pendingTier === "portal" ? (
                  <LoadingDots label="Opening billing" />
                ) : (
                  "Manage billing"
                )}
              </button>
            )}
          </>
        )}
        {!canUpgrade && !current && id === "free" && (
          <p className="tier-current-label tier-free-note">
            No credit card needed
          </p>
        )}
      </div>
    </div>
  );
}

function OneTimePurchaseCard({
  serverOptions,
  selectedServerId,
  onSelectServer,
  onPurchase,
  pending,
  successMessage,
}: {
  serverOptions: { id: string; name: string }[];
  selectedServerId: string;
  onSelectServer: (serverId: string) => void;
  onPurchase: () => void;
  pending: boolean;
  successMessage: string | null;
}) {
  return (
    <div className="tier-card tier-card-cyan tier-card-purchase">
      <div className="tier-badge tier-badge-cyan">One-time</div>

      <div className="tier-card-top">
        <strong className="tier-name">Full Server Scan</strong>
        <div className="tier-price-block">
          <span className="tier-price-num">$9.99</span>
          <span className="tier-price-period">one time</span>
        </div>
        <p className="tier-tagline">
          Deep-index your entire message history so Kodama starts with full
          context.
        </p>
      </div>

      <ul className="tier-feature-list">
        <li>
          <Check size={14} aria-hidden="true" />
          Reads every channel from the beginning
        </li>
        <li>
          <Check size={14} aria-hidden="true" />
          No page cap on history
        </li>
        <li>
          <Check size={14} aria-hidden="true" />
          Best for older servers with years of lore
        </li>
      </ul>

      <div className="tier-card-cta">
        {serverOptions.length > 1 && (
          <CustomSelect
            label="Scan server"
            value={selectedServerId}
            options={serverOptions.map((server) => ({
              value: server.id,
              label: server.name,
            }))}
            onChange={onSelectServer}
          />
        )}
        {successMessage && (
          <p className="store-scan-success">{successMessage}</p>
        )}
        <button
          type="button"
          className="primary-button store-scan-btn tier-card-button"
          disabled={pending || !selectedServerId}
          onClick={onPurchase}
        >
          {pending ? "Redirecting…" : "Buy scan"}
        </button>
      </div>
    </div>
  );
}

function StoreFooter() {
  return (
    <footer className="marketing-footer store-footer">
      <div className="footer-brand">
        <span className="public-brand footer-brand-mark">
          <img src="/kodama_logo.png" alt="" aria-hidden="true" />
          <strong>Kodama</strong>
        </span>
        <p>Your server&apos;s lore lives on.</p>
        <p className="footer-copy-line">
          &copy; {new Date().getFullYear()} Kodama. All rights reserved.
        </p>
      </div>
      <nav className="footer-links" aria-label="Footer navigation">
        <div>
          <h4>Product</h4>
          <a href={`${marketingOrigin}/commands`}>Commands</a>
          <a href={botInviteUrl()}>Add to Discord</a>
          <a href="/servers">Dashboard</a>
          <a
            href="https://discord.gg/xT9vgkTHqF"
            target="_blank"
            rel="noopener noreferrer"
          >
            Support server
          </a>
        </div>
        <div>
          <h4>Legal</h4>
          <a href={`${marketingOrigin}/terms`}>Terms of Service</a>
          <a href={`${marketingOrigin}/privacy`}>Privacy Policy</a>
        </div>
      </nav>
    </footer>
  );
}

function UpgradePanel({
  tier,
  onUpgrade,
  onManageBilling,
  pendingTier,
  scanServerOptions,
  scanServerId,
  onSelectScanServer,
  onScanCheckout,
  scanPending,
  scanSuccessMessage,
}: {
  tier: string;
  onUpgrade: (tier: "index" | "chronicle") => void;
  onManageBilling: () => void;
  pendingTier: "index" | "chronicle" | "portal" | null;
  scanServerOptions: { id: string; name: string }[];
  scanServerId: string;
  onSelectScanServer: (serverId: string) => void;
  onScanCheckout: () => void;
  scanPending: boolean;
  scanSuccessMessage: string | null;
}) {
  return (
    <section className="upgrade-panel">
      <p className="upgrade-subtitle">
        Pick the plan that fits your server, or grab a full-history scan when
        you want Kodama caught up from day one.
      </p>
      <div className="upgrade-tiers">
        <TierCard
          id="free"
          name="Free"
          priceNum={0}
          tagline="Get started, no commitment."
          features={FREE_FEATURES}
          current={tier === "free"}
          userTier={tier}
          pendingTier={pendingTier}
          onUpgrade={onUpgrade}
          onManageBilling={onManageBilling}
        />
        <TierCard
          id="index"
          name="Kodama Index"
          priceNum={9.99}
          tagline="Full memory and personality for active servers."
          features={INDEX_FEATURES}
          inheritsFrom="Free"
          badge="Most popular"
          accent="mint"
          current={tier === "index"}
          userTier={tier}
          pendingTier={pendingTier}
          onUpgrade={onUpgrade}
          onManageBilling={onManageBilling}
        />
        <TierCard
          id="chronicle"
          name="Kodama Chronicle"
          priceNum={19.99}
          tagline="For servers that live and breathe their own lore."
          features={CHRONICLE_FEATURES}
          inheritsFrom="Kodama Index"
          badge="Premium"
          accent="gold"
          current={tier === "chronicle"}
          userTier={tier}
          pendingTier={pendingTier}
          onUpgrade={onUpgrade}
          onManageBilling={onManageBilling}
        />
        <OneTimePurchaseCard
          serverOptions={scanServerOptions}
          selectedServerId={scanServerId}
          onSelectServer={onSelectScanServer}
          onPurchase={onScanCheckout}
          pending={scanPending}
          successMessage={scanSuccessMessage}
        />
      </div>
    </section>
  );
}

function BillingPage({
  me,
  meLoading = false,
  selectedServerId,
  onNavigate,
  showToast,
}: {
  me: MeResponse | null;
  meLoading?: boolean;
  selectedServerId?: string | null;
  onNavigate: (page: PageId) => void;
  showToast: (message: string, tone?: "neutral" | "success" | "error") => void;
}) {
  // Subscription is user-level: use the user's own tier, not the server's tier
  const tier =
    (me as (MeResponse & { userTier?: string }) | null)?.userTier ?? "free";
  const [pendingTier, setPendingTier] = useState<
    "index" | "chronicle" | "portal" | null
  >(null);
  const [scanPending, setScanPending] = useState(false);
  const [scanServerId, setScanServerId] = useState<string>(
    selectedServerId ?? me?.servers[0]?.id ?? "",
  );
  const scanServerName =
    me?.servers.find((s) => s.id === scanServerId)?.name ?? "your server";

  const scanSuccess =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("scan_success") === "1";
  const scanServerOptions =
    me?.servers.map((server) => ({
      id: server.id,
      name: server.name,
    })) ?? [];

  const billingServerId = selectedServerId ?? me?.servers[0]?.id ?? "";

  async function openCheckout(nextTier: "index" | "chronicle") {
    setPendingTier(nextTier);
    try {
      const { url } = await createCheckoutSession(nextTier);
      window.location.href = url;
    } catch (err) {
      showToast(
        err instanceof ApiError && err.message
          ? err.message
          : "Could not start checkout. Try again in a moment.",
        "error",
      );
      setPendingTier(null);
    }
  }

  async function openBillingPortal() {
    setPendingTier("portal");
    try {
      const { url } = await createBillingPortalSession();
      window.location.href = url;
    } catch (err) {
      showToast(
        err instanceof ApiError && err.message
          ? err.message
          : "Could not open billing portal. Try again in a moment.",
        "error",
      );
      setPendingTier(null);
    }
  }

  async function openScanCheckout() {
    if (!scanServerId) return;
    setScanPending(true);
    try {
      const { url } = await createScanCheckoutSession(scanServerId);
      window.location.href = url;
    } catch (err) {
      showToast(
        err instanceof ApiError && err.message
          ? err.message
          : "Could not start scan checkout. Try again in a moment.",
        "error",
      );
      setScanPending(false);
    }
  }

  return (
    <main className="marketing-shell">
      <MarketingNav me={me} meLoading={meLoading} onNavigate={onNavigate} />
      <section className="store-page-content">
        <div className="store-page-header">
          <p className="eyebrow">Account</p>
          <h2>Store</h2>
        </div>
        <UpgradePanel
          tier={tier}
          onUpgrade={openCheckout}
          onManageBilling={openBillingPortal}
          pendingTier={pendingTier}
          scanServerOptions={scanServerOptions}
          scanServerId={scanServerId}
          onSelectScanServer={setScanServerId}
          onScanCheckout={() => void openScanCheckout()}
          scanPending={scanPending}
          scanSuccessMessage={
            scanSuccess
              ? `Payment received — full scan queued for ${scanServerName}.`
              : null
          }
        />
        <StoreFooter />
      </section>
    </main>
  );
}

/**
 * Handles /api/auth/callback — Discord redirects here after OAuth.
 * CF Pages serves index.html for this path; we exchange the code for a token.
 */
function OAuthCallbackPage() {
  const [status, setStatus] = useState<"loading" | "error">("loading");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) {
      setStatus("error");
      return;
    }
    exchangeOAuthCode(code, state)
      .then(({ token, returnTo }) => {
        storeToken(token);
        window.location.replace(safeReturnTo(returnTo));
      })
      .catch(() => setStatus("error"));
  }, []);

  return (
    <main
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: "12px",
      }}
    >
      {status === "loading" ? (
        <div className="auth-loading">
          <LoadingDots label="Signing in" />
          <p>Signing in...</p>
        </div>
      ) : (
        <>
          <p>
            Sign-in failed. <a href="/">Go back</a>
          </p>
        </>
      )}
    </main>
  );
}

/**
 * Handles the /auth/finish route after Discord OAuth callback.
 * Reads the session token from the URL hash, stores it, and redirects.
 */
function safeReturnTo(value: string | null): string {
  if (!value) return "/servers";
  // Must be a relative path, not a protocol-relative or absolute URL
  if (!value.startsWith("/") || value.startsWith("//")) return "/servers";
  if (value.length > 180) return "/servers";
  return value;
}

function AuthFinishPage() {
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);
    const token = params.get("token");
    const returnTo = safeReturnTo(
      new URLSearchParams(window.location.search).get("return_to"),
    );
    if (token) {
      storeToken(decodeURIComponent(token));
    }
    // Remove the token hash from browser history before navigating
    history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
    window.location.replace(returnTo);
  }, []);

  return (
    <main
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <div className="auth-loading">
        <LoadingDots label="Signing in" />
        <p>Signing in...</p>
      </div>
    </main>
  );
}

function AdminOperationsSnapshot({
  usageData,
  loading,
  error,
  servers,
  subscriptions,
  activeSessions,
  onOpenUsage,
}: {
  usageData: AdminUsageData | null;
  loading: boolean;
  error: string | null;
  servers: AdminServer[];
  subscriptions: AdminSubscription[];
  activeSessions: number;
  onOpenUsage: () => void;
}) {
  const totals = usageData?.totals;
  const cacheRate = totals?.inputTokens
    ? (totals.cachedInputTokens / totals.inputTokens) * 100
    : 0;
  const neverIndexed = servers.filter(
    (server) => !server.last_indexed_at,
  ).length;
  const activeSubscriptions = subscriptions.filter(
    (subscription) =>
      subscription.status === "active" || subscription.status === "trialing",
  ).length;

  return (
    <section className="admin-operations-section">
      <div className="admin-section-heading">
        <div>
          <p className="eyebrow">Last 24 hours</p>
          <h3>Operations snapshot</h3>
        </div>
        <button type="button" className="admin-link-btn" onClick={onOpenUsage}>
          Open usage details
          <ArrowRight size={14} aria-hidden="true" />
        </button>
      </div>

      {error && !usageData && <p className="admin-error">{error}</p>}
      <div className="admin-ops-grid">
        <article className="admin-ops-card">
          <span>API cost</span>
          <strong>
            {loading && !totals ? "—" : formatUsd(totals?.costUsd ?? 0)}
          </strong>
          <small>{formatNumber(totals?.calls ?? 0)} calls</small>
        </article>
        <article className="admin-ops-card">
          <span>Cache hit rate</span>
          <strong>
            {loading && !totals ? "—" : `${cacheRate.toFixed(1)}%`}
          </strong>
          <small>
            {formatNumber(totals?.cachedInputTokens ?? 0)} cached tokens
          </small>
        </article>
        <article className="admin-ops-card">
          <span>Average latency</span>
          <strong>
            {totals?.avgLatencyMs == null
              ? "—"
              : `${formatNumber(totals.avgLatencyMs)} ms`}
          </strong>
          <small>Across all providers</small>
        </article>
        <article className="admin-ops-card">
          <span>Active purchases</span>
          <strong>{formatNumber(activeSubscriptions)}</strong>
          <small>{neverIndexed} servers never indexed</small>
        </article>
        <article className="admin-ops-card">
          <span>Dashboard sessions</span>
          <strong>{formatNumber(activeSessions)}</strong>
          <small>Session rows in DB</small>
        </article>
      </div>

      <div className="admin-overview-panels">
        <article className="admin-panel-card">
          <div className="admin-panel-card-head">
            <div>
              <span>Highest spend</span>
              <strong>Request paths</strong>
            </div>
            <Activity size={17} aria-hidden="true" />
          </div>
          {usageData?.paths.length ? (
            <div className="admin-ranked-list">
              {usageData.paths.slice(0, 5).map((row) => (
                <div key={row.request_path}>
                  <code>{row.request_path}</code>
                  <span>{formatUsd(row.cost_usd, 6)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="admin-panel-empty">
              No request telemetry in this window.
            </p>
          )}
        </article>
        <article className="admin-panel-card">
          <div className="admin-panel-card-head">
            <div>
              <span>Efficiency</span>
              <strong>Token composition</strong>
            </div>
            <Zap size={17} aria-hidden="true" />
          </div>
          <div className="admin-token-mix">
            <div>
              <span>Input</span>
              <strong>{formatNumber(totals?.inputTokens ?? 0)}</strong>
            </div>
            <div>
              <span>Output</span>
              <strong>{formatNumber(totals?.outputTokens ?? 0)}</strong>
            </div>
            <div>
              <span>Thinking</span>
              <strong>{formatNumber(totals?.thinkingTokens ?? 0)}</strong>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

function contextBlockLabel(block: string): string {
  return block
    .replace(/Chars$/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function AdminUsagePanel({
  data,
  loading,
  error,
  window,
  lastRefreshed,
  onWindowChange,
  onRefresh,
}: {
  data: AdminUsageData | null;
  loading: boolean;
  error: string | null;
  window: UsageWindow;
  lastRefreshed: Date | null;
  onWindowChange: (window: UsageWindow) => void;
  onRefresh: () => void;
}) {
  const chartData = useMemo(() => {
    if (!data?.hourly.length) return [];
    const byHour = new Map<
      string,
      Record<string, string | number> & { _isoHour: string; hour: string }
    >();
    for (const row of data.hourly) {
      const current = byHour.get(row.hour) ?? {
        _isoHour: row.hour,
        hour: formatHour(row.hour, data.hourlyWindow),
        anthropic: 0,
        gemini: 0,
        openai: 0,
        voyage: 0,
        other: 0,
      };
      const provider = ["anthropic", "gemini", "openai", "voyage"].includes(
        row.provider,
      )
        ? row.provider
        : "other";
      current[provider] = Number(current[provider] ?? 0) + Number(row.cost_usd);
      byHour.set(row.hour, current);
    }
    return [...byHour.values()];
  }, [data]);

  const totals = data?.totals;
  const cacheRate = totals?.inputTokens
    ? (totals.cachedInputTokens / totals.inputTokens) * 100
    : 0;
  const costPerCall = totals?.calls ? totals.costUsd / totals.calls : 0;
  const maxContextChars = Math.max(
    1,
    ...(data?.contextBlocks.map((row) => Number(row.total_chars)) ?? []),
  );
  const windows: Array<{ id: UsageWindow; label: string }> = [
    { id: "24h", label: "24h" },
    { id: "7d", label: "7d" },
    { id: "30d", label: "30d" },
    { id: "all", label: "All time" },
  ];

  return (
    <section className="admin-usage-panel">
      <div className="admin-usage-toolbar">
        <div>
          <p className="eyebrow">Model economics</p>
          <h3>Token usage and cost</h3>
          <span>
            One authenticated snapshot across providers, paths, and prompt
            context.
          </span>
        </div>
        <div className="admin-usage-controls">
          <div className="admin-window-picker" aria-label="Usage window">
            {windows.map((item) => (
              <button
                key={item.id}
                type="button"
                className={window === item.id ? "active" : ""}
                aria-pressed={window === item.id}
                onClick={() => onWindowChange(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="admin-refresh-btn admin-refresh-icon"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Refresh usage"
            title="Refresh usage"
          >
            <RefreshCw
              size={15}
              aria-hidden="true"
              className={loading ? "is-spinning" : ""}
            />
          </button>
        </div>
      </div>

      {lastRefreshed && (
        <p className="admin-usage-updated">
          Updated {lastRefreshed.toLocaleTimeString()}
          {data?.hourlyWindow !== data?.window
            ? ` · chart limited to ${data?.hourlyWindow}`
            : ""}
        </p>
      )}
      {error && <p className="admin-error">{error}</p>}
      {loading && !data && (
        <div className="admin-loading">
          <LoadingDots label="Loading usage data" />
        </div>
      )}

      {data && (
        <>
          <div className="admin-usage-kpis">
            <article>
              <span>Daily guardrail</span>
              <strong>{data.spend.mode}</strong>
              <small>
                {formatUsd(data.spend.dailyCostUsd)} today · stop at{" "}
                {formatUsd(data.spend.budgets.emergencyUsd)}
              </small>
            </article>
            <article>
              <span>Cost</span>
              <strong>{formatUsd(totals?.costUsd ?? 0)}</strong>
              <small>{formatUsd(costPerCall, 6)} / call</small>
            </article>
            <article>
              <span>Calls</span>
              <strong>{formatNumber(totals?.calls ?? 0)}</strong>
              <small>{data.rows.length} model/provider pairs</small>
            </article>
            <article>
              <span>Total tokens</span>
              <strong>{formatNumber(totals?.totalTokens ?? 0)}</strong>
              <small>input + output + provider extras</small>
            </article>
            <article>
              <span>Cache hit</span>
              <strong>{cacheRate.toFixed(1)}%</strong>
              <small>
                {formatNumber(totals?.cachedInputTokens ?? 0)} cached input
              </small>
            </article>
            <article>
              <span>Thinking</span>
              <strong>{formatNumber(totals?.thinkingTokens ?? 0)}</strong>
              <small>
                {formatNumber(totals?.toolUseTokens ?? 0)} tool-use tokens
              </small>
            </article>
            <article>
              <span>Avg latency</span>
              <strong>
                {totals?.avgLatencyMs == null
                  ? "—"
                  : `${formatNumber(totals.avgLatencyMs)} ms`}
              </strong>
              <small>across recorded calls</small>
            </article>
          </div>

          <article className="admin-panel-card admin-chart-panel">
            <div className="admin-panel-card-head">
              <div>
                <span>Cost over time</span>
                <strong>Provider spend (USD)</strong>
              </div>
            </div>
            {chartData.length < 2 ? (
              <p className="admin-chart-empty">
                The chart will appear after usage spans at least two hours.
              </p>
            ) : (
              <div className="admin-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 8, right: 22, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.06)"
                    />
                    <XAxis
                      dataKey="hour"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={formatYAxisTick}
                      width={74}
                    />
                    <Tooltip
                      formatter={(value: unknown, name: unknown) => [
                        formatUsd(Number(value), Number(value) < 0.01 ? 6 : 4),
                        String(name),
                      ]}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="anthropic"
                      name="Anthropic"
                      stroke="#6b7cff"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="gemini"
                      name="Gemini"
                      stroke="#f5b942"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="voyage"
                      name="Voyage"
                      stroke="#22d3a5"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="openai"
                      name="OpenAI"
                      stroke="#f97316"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="other"
                      name="Other"
                      stroke="#a78bfa"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </article>

          <div className="admin-usage-detail-grid">
            <article className="admin-panel-card">
              <div className="admin-panel-card-head">
                <div>
                  <span>Cost centers</span>
                  <strong>Request paths</strong>
                </div>
              </div>
              {data.paths.length ? (
                <div className="admin-ranked-list admin-ranked-list-wide">
                  {data.paths.slice(0, 10).map((row) => (
                    <div key={row.request_path}>
                      <span className="admin-ranked-main">
                        <code>{row.request_path}</code>
                        <small>
                          {formatNumber(row.calls)} calls ·{" "}
                          {row.avg_latency_ms == null
                            ? "—"
                            : `${formatNumber(row.avg_latency_ms)} ms`}
                        </small>
                      </span>
                      <strong>{formatUsd(row.cost_usd, 6)}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="admin-panel-empty">No request telemetry yet.</p>
              )}
            </article>
            <article className="admin-panel-card">
              <div className="admin-panel-card-head">
                <div>
                  <span>Prompt payload</span>
                  <strong>Context composition</strong>
                </div>
              </div>
              {data.contextBlocks.length ? (
                <div className="admin-context-list">
                  {data.contextBlocks.map((row) => {
                    const value = Number(row.total_chars);
                    return (
                      <div className="admin-context-row" key={row.block}>
                        <div>
                          <span>{contextBlockLabel(row.block)}</span>
                          <small>{formatNumber(value)} chars</small>
                        </div>
                        <span className="admin-context-track">
                          <span
                            style={{
                              width: `${Math.max(2, (value / maxContextChars) * 100)}%`,
                            }}
                          />
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="admin-panel-empty">
                  Context telemetry will appear on new calls.
                </p>
              )}
            </article>
          </div>

          <div className="admin-table-section">
            <div className="admin-section-heading">
              <div>
                <p className="eyebrow">Detail</p>
                <h3>Models</h3>
              </div>
              <span>{data.rows.length} model/provider pairs</span>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Provider</th>
                    <th>Calls</th>
                    <th>Input</th>
                    <th>Cached</th>
                    <th>Output</th>
                    <th>Thinking</th>
                    <th>Latency</th>
                    <th>Cost</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="admin-empty-row">
                        No usage data in this window.
                      </td>
                    </tr>
                  ) : (
                    data.rows.map((row) => (
                      <tr
                        key={`${row.provider}:${row.model}`}
                        className="admin-row"
                      >
                        <td>
                          <strong>{row.model}</strong>
                        </td>
                        <td>
                          <span
                            className={`status-pill ${row.provider === "anthropic" ? "status-good" : row.provider === "voyage" ? "admin-tier-index" : "status-muted"}`}
                          >
                            {row.provider}
                          </span>
                        </td>
                        <td className="admin-td-num">
                          {formatNumber(row.calls)}
                        </td>
                        <td className="admin-td-num">
                          {formatNumber(row.input_tokens)}
                        </td>
                        <td className="admin-td-num">
                          {formatNumber(row.cached_input_tokens)}
                        </td>
                        <td className="admin-td-num">
                          {formatNumber(row.output_tokens)}
                        </td>
                        <td className="admin-td-num">
                          {formatNumber(row.thinking_tokens)}
                        </td>
                        <td className="admin-td-num">
                          {row.avg_latency_ms == null
                            ? "—"
                            : `${formatNumber(row.avg_latency_ms)} ms`}
                        </td>
                        <td className="admin-td-num">
                          <strong>{formatUsd(row.cost_usd, 6)}</strong>
                        </td>
                        <td className="admin-td-date">
                          {formatDate(row.last_seen)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

// ── Admin usage formatting helpers ──────────────────────────────────────────

function formatUsd(n: number | string, decimals = 4): string {
  return `$${Number(n).toFixed(decimals)}`;
}

function formatNumber(n: number | string): string {
  return Number(n).toLocaleString();
}

function formatHour(iso: string, window: string): string {
  try {
    const d = new Date(iso);
    if (window === "24h") {
      return d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    return d.toLocaleDateString(undefined, {
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatYAxisTick(value: number): string {
  if (value === 0) return "$0";
  return value < 0.01 ? `$${value.toFixed(6)}` : `$${value.toFixed(4)}`;
}

function App() {
  const [page, setPage] = useState<PageId>(getInitialPage);
  const [activeTab, setActiveTab] = useState<TabId>(getInitialTab);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [meLoadFailed, setMeLoadFailed] = useState(false);
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ServerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [memoryWorking, setMemoryWorking] = useState(false);
  const [memoryError, setMemoryError] = useState<string | null>(null);
  const [askOpen, setAskOpen] = useState(false);
  const [askMessages, setAskMessages] = useState<AskMessage[]>([]);
  const [askSending, setAskSending] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [askCapped, setAskCapped] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [scanRunning, setScanRunning] = useState(false);
  const [appChannels, setAppChannels] = useState<GuildChannel[]>([]);
  const [appToast, setAppToast] = useState<{
    message: string;
    tone: "neutral" | "success" | "error";
    icon?: ReactNode;
  } | null>(null);
  const [appToastDismissing, setAppToastDismissing] = useState(false);
  const appToastTimerRef = useRef<number | null>(null);
  const [authError, setAuthError] = useState(false);
  const [authToastDismissing, setAuthToastDismissing] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [upgradeModal, setUpgradeModal] = useState<{
    requiredTier?: string;
  } | null>(null);

  useEffect(() => {
    function showAuthError() {
      setAuthToastDismissing(false);
      setAuthError(true);
    }
    function showUpgradeModal(e: Event) {
      const detail = (e as CustomEvent).detail as
        | { requiredTier?: string }
        | undefined;
      setUpgradeModal({ requiredTier: detail?.requiredTier });
    }

    window.addEventListener("kodama:auth-start-error", showAuthError);
    window.addEventListener("kodama:upgrade-required", showUpgradeModal);
    return () => {
      window.removeEventListener("kodama:auth-start-error", showAuthError);
      window.removeEventListener("kodama:upgrade-required", showUpgradeModal);
    };
  }, []);

  useEffect(() => {
    if (!authError) return;
    const timeout = window.setTimeout(dismissAuthToast, 5200);
    return () => window.clearTimeout(timeout);
  }, [authError]);

  useEffect(() => {
    function syncFromLocation() {
      setPage(getInitialPage());
      setActiveTab(getInitialTab());
      setAuthError(false);
      setAuthToastDismissing(false);
    }

    window.addEventListener("popstate", syncFromLocation);
    return () => window.removeEventListener("popstate", syncFromLocation);
  }, []);

  useEffect(() => {
    if (page !== "dashboard") return;
    const query = selectedServerId ? `?server=${selectedServerId}` : "";
    const nextHash = `#${activeTab}`;
    if (window.location.search !== query || window.location.hash !== nextHash) {
      window.history.replaceState(null, "", `/dashboard${query}${nextHash}`);
    }
    if (selectedServerId) {
      localStorage.setItem("kodama_last_server", selectedServerId);
    }
  }, [activeTab, page, selectedServerId]);

  useEffect(() => {
    if (page === "auth-finish" || page === "oauth-callback") return;
    getMe()
      .then((data) => {
        setMe(data);
        const urlServerId = new URLSearchParams(window.location.search).get(
          "server",
        );
        const savedServerId = localStorage.getItem("kodama_last_server");
        // Prefer URL param → saved server → first server
        const candidateId = urlServerId ?? savedServerId;
        const requestedServer = data.servers.find(
          (server) => server.id === candidateId,
        );
        // Admins can open any server by ID even if they don't own it —
        // trust the URL param directly instead of falling back to their own servers.
        const resolvedId =
          data.isAdmin && candidateId
            ? candidateId
            : (requestedServer?.id ?? data.servers[0]?.id ?? null);
        setSelectedServerId(resolvedId);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 401) {
          if (page === "servers" || page === "dashboard") {
            void initiateLogin(currentReturnTo());
          }
          return;
        }
        // Non-401 error (500, network, etc.): flag so UI shows proper message
        setMeLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => {
    if (!selectedServerId) return;
    setAskMessages([]);
    setAskError(null);
    setAskCapped(false);
    setLoading(true);
    setDashboardError(null);
    getServer(selectedServerId)
      .then((data) => {
        setDetail(data);
        setScanRunning(data.overview.scanInProgress);
      })
      .catch((error: unknown) => {
        setDetail(null);
        if (error instanceof ApiError && error.status === 401) {
          void initiateLogin(currentReturnTo());
          return;
        }
        if (error instanceof ApiError && [403, 404].includes(error.status)) {
          navigate("servers");
          return;
        }
        setDashboardError("Discord is taking a moment. Try this server again.");
      })
      .finally(() => setLoading(false));
  }, [loadAttempt, page, selectedServerId]);

  // ── Background polling — silently refresh server detail ──────────────────
  // 10s while a scan is running, 30s otherwise.  Skips if the tab is hidden
  // to avoid waking the server for nobody.
  useEffect(() => {
    if (page !== "dashboard" || !selectedServerId) return;
    const interval = scanRunning ? 10_000 : 30_000;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      getServer(selectedServerId)
        .then((fresh) => {
          setDetail((prev) => {
            // Preserve any in-progress form edits by only swapping the fields
            // that come from the server — don't clobber local state.
            if (!prev) return fresh;
            return {
              ...fresh,
              // keep the same server summary reference so settings panels
              // don't reset while the user is mid-edit
              server: prev.server,
            };
          });
          // Drive scanRunning from authoritative server state so the cancel
          // button stays visible for the full duration of the scan.
          setScanRunning(fresh.overview.scanInProgress);
        })
        .catch(() => {
          // Silently ignore background poll errors — don't surface to user
        });
    }, interval);
    return () => window.clearInterval(id);
  }, [page, selectedServerId, scanRunning]);

  useEffect(() => {
    if (page === "dashboard" && !loading && me && !selectedServerId) {
      navigate("servers");
    }
  }, [loading, me, page, selectedServerId]);

  // Fetch guild channels when the selected server changes.
  useEffect(() => {
    if (!selectedServerId) {
      setAppChannels([]);
      return;
    }
    fetchServerChannels(selectedServerId)
      .then(setAppChannels)
      .catch(() => setAppChannels([]));
  }, [selectedServerId]);

  const selectedServer = useMemo(
    () => me?.servers.find((server) => server.id === selectedServerId) ?? null,
    [me, selectedServerId],
  );

  const title = {
    overview: "Dashboard",
    memory: "Memory",
    recaps: "Schedule",
    privacy: "Guardrails",
    settings: "Controls",
    profile: "Bot Profile",
    logs: "Logs",
  }[activeTab];

  function showToast(
    message: string,
    tone: "neutral" | "success" | "error" = "success",
    icon?: ReactNode,
  ): void {
    if (appToastTimerRef.current) window.clearTimeout(appToastTimerRef.current);
    setAppToastDismissing(false);
    setAppToast({ message, tone, icon });
    appToastTimerRef.current = window.setTimeout(() => {
      setAppToastDismissing(true);
      appToastTimerRef.current = window.setTimeout(() => {
        setAppToast(null);
        setAppToastDismissing(false);
      }, 260);
    }, 4000);
  }

  function dismissAppToast(): void {
    if (appToastTimerRef.current) window.clearTimeout(appToastTimerRef.current);
    setAppToastDismissing(true);
    appToastTimerRef.current = window.setTimeout(() => {
      setAppToast(null);
      setAppToastDismissing(false);
    }, 260);
  }

  function navigate(pageId: PageId): void {
    const path = `/${pageId}`;
    window.history.pushState(null, "", path);
    setPage(pageId);
    setAuthError(false);
    setAuthToastDismissing(false);
    if (pageId === "dashboard") {
      setActiveTab("overview");
    }
  }

  function dismissAuthToast(): void {
    setAuthToastDismissing(true);
    window.setTimeout(() => {
      setAuthError(false);
      setAuthToastDismissing(false);
    }, 260);
  }

  function manageServer(serverId: string): void {
    setSelectedServerId(serverId);
    setDetail(null);
    setDashboardError(null);
    setSaveError(null);
    setMemoryError(null);
    window.history.pushState(
      null,
      "",
      `/dashboard?server=${serverId}#overview`,
    );
    setActiveTab("overview");
    setPage("dashboard");
  }

  async function saveServer(nextServer: ServerSummary): Promise<void> {
    if (!detail) return;
    const currentServer = detail.server;
    const patch: SettingsPatch = {};
    if (nextServer.personality !== currentServer.personality) {
      patch.personality = nextServer.personality;
    }
    if (nextServer.custom_personality !== currentServer.custom_personality) {
      patch.custom_personality = nextServer.custom_personality;
    }
    if (nextServer.flame_enabled !== currentServer.flame_enabled) {
      patch.flame_enabled = nextServer.flame_enabled;
    }
    if (nextServer.recap_channel_id !== currentServer.recap_channel_id) {
      patch.recap_channel_id = nextServer.recap_channel_id;
    }
    if (nextServer.recap_time !== currentServer.recap_time) {
      patch.recap_time = nextServer.recap_time;
    }
    if (nextServer.bot_display_name !== currentServer.bot_display_name) {
      patch.bot_display_name = nextServer.bot_display_name;
    }
    if (nextServer.bot_avatar_url !== currentServer.bot_avatar_url) {
      patch.bot_avatar_url = nextServer.bot_avatar_url;
    }
    if (nextServer.bot_status !== currentServer.bot_status) {
      patch.bot_status = nextServer.bot_status;
    }
    if (nextServer.bot_description !== currentServer.bot_description) {
      patch.bot_description = nextServer.bot_description;
    }
    if (nextServer.bot_banner !== currentServer.bot_banner) {
      patch.bot_banner = nextServer.bot_banner;
    }
    if (nextServer.mention_behavior !== currentServer.mention_behavior) {
      patch.mention_behavior = nextServer.mention_behavior;
    }
    if (
      nextServer.emoji_bank.join(" ") !== currentServer.emoji_bank.join(" ")
    ) {
      patch.emoji_bank = nextServer.emoji_bank;
    }
    if (nextServer.lock_emoji_bank !== currentServer.lock_emoji_bank) {
      patch.lock_emoji_bank = nextServer.lock_emoji_bank;
    }
    if (nextServer.channel_filter_mode !== currentServer.channel_filter_mode) {
      patch.channel_filter_mode = nextServer.channel_filter_mode;
    }
    if (
      JSON.stringify([...(nextServer.channel_filter_ids ?? [])].sort()) !==
      JSON.stringify([...(currentServer.channel_filter_ids ?? [])].sort())
    ) {
      patch.channel_filter_ids = nextServer.channel_filter_ids;
    }
    if (
      (nextServer.system_prompt ?? null) !==
      (currentServer.system_prompt ?? null)
    ) {
      patch.system_prompt = nextServer.system_prompt;
    }
    if (Object.keys(patch).length === 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      const result = await updateSettings(nextServer.id, patch);
      if (result.server) {
        setDetail({ ...detail, server: result.server });
      }
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Could not save settings",
      );
    } finally {
      setSaving(false);
    }
  }

  async function saveSettingsPatch(patch: SettingsPatch): Promise<void> {
    if (!detail) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await updateSettings(detail.server.id, patch);
      if (result.server) {
        setDetail({ ...detail, server: result.server });
      }
    } catch {
      setSaveError("Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  async function addMemory(entry: {
    title: string;
    summary: string;
  }): Promise<void> {
    if (!detail) return;
    setMemoryWorking(true);
    setMemoryError(null);
    try {
      const result = await createLore(detail.server.id, entry);
      const alreadySaved = detail.lore.some(
        (item) => item.id === result.lore.id,
      );
      setDetail({
        ...detail,
        lore: [
          result.lore,
          ...detail.lore.filter((item) => item.id !== result.lore.id),
        ],
        overview: {
          ...detail.overview,
          loreCount: alreadySaved
            ? detail.overview.loreCount
            : detail.overview.loreCount + 1,
          auditCountToday: detail.overview.auditCountToday + 1,
        },
      });
    } catch (error) {
      setMemoryError(
        error instanceof Error ? error.message : "Could not save memory",
      );
      throw error;
    } finally {
      setMemoryWorking(false);
    }
  }

  async function removeMemory(entry: LoreEntry): Promise<void> {
    if (!detail) return;
    setMemoryWorking(true);
    setMemoryError(null);
    try {
      await deleteLore(detail.server.id, entry.id);
      setDetail({
        ...detail,
        lore: detail.lore.filter((item) => item.id !== entry.id),
        overview: {
          ...detail.overview,
          loreCount: Math.max(0, detail.overview.loreCount - 1),
          auditCountToday: detail.overview.auditCountToday + 1,
        },
      });
    } catch (error) {
      setMemoryError(
        error instanceof Error ? error.message : "Could not delete memory",
      );
      throw error;
    } finally {
      setMemoryWorking(false);
    }
  }

  // Immediately re-fetch server state and sync scanRunning to the
  // server's authoritative scansInProgress value. Called after any scan
  // action so the cancel button reflects truth without waiting for the
  // next 10s poll tick.
  async function pollScanState(): Promise<void> {
    if (!selectedServerId) return;
    try {
      const fresh = await getServer(selectedServerId);
      setDetail((prev) => (prev ? { ...fresh, server: prev.server } : fresh));
      setScanRunning(fresh.overview.scanInProgress);
    } catch {
      // ignore — the background poll will correct things within 10s
    }
  }

  function markScanQueued(): void {
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            overview: {
              ...prev.overview,
              scanInProgress: true,
              scanLifecycle: {
                ...(prev.overview.scanLifecycle ?? emptyScanLifecycle),
                state: "queued",
                currentStartedAt: null,
                failedReason: null,
              },
            },
          }
        : prev,
    );
  }

  async function handleScan(): Promise<void> {
    if (!detail || scanRunning) return;
    setScanRunning(true);
    try {
      await scanServerHistory(detail.server.id);
      markScanQueued();
      showToast(
        "Scan started — this may take a few minutes",
        "success",
        <History size={18} aria-hidden="true" />,
      );
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        err.message &&
        !err.message.startsWith("API ")
          ? err.message
          : "Scan failed. Try again.";
      showToast(msg, "error");
    } finally {
      // Re-poll to get the real server state — handles the 409 "already
      // running" case (keeps cancel visible) and real failures (hides it).
      void pollScanState();
    }
  }

  async function handleFullScan(): Promise<void> {
    if (!detail || scanRunning) return;
    setScanRunning(true);
    try {
      await fullScanServer(detail.server.id);
      markScanQueued();
      showToast(
        "Full scan started — indexing all history, this may take a while",
        "success",
        <History size={18} aria-hidden="true" />,
      );
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        err.message &&
        !err.message.startsWith("API ")
          ? err.message
          : "Full scan failed. Try again.";
      showToast(msg, "error");
    } finally {
      void pollScanState();
    }
  }

  async function handleCancelScan(): Promise<void> {
    if (!detail || !scanRunning) return;
    try {
      await cancelScan(detail.server.id);
      showToast(
        "Scan cancellation requested — stopping after current page",
        "success",
      );
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        err.message &&
        !err.message.startsWith("API ")
          ? err.message
          : "Could not cancel scan.";
      showToast(msg, "error");
    }
  }

  async function sendAskQuestion(question: string): Promise<void> {
    if (!detail) return;
    const nextMessages: AskMessage[] = [
      ...askMessages,
      { role: "user", content: question },
    ];
    setAskMessages(nextMessages);
    setAskSending(true);
    setAskError(null);
    try {
      const result = await askKodama(detail.server.id, question, askMessages);
      setAskMessages([
        ...nextMessages,
        { role: "kodama", content: result.answer },
      ]);
    } catch (error) {
      const isRateLimited = error instanceof ApiError && error.status === 429;
      if (isRateLimited) {
        setAskCapped(true);
      } else {
        setAskError(
          error instanceof Error ? error.message : "Kodama could not answer",
        );
        setAskMessages([
          ...nextMessages,
          {
            role: "kodama",
            content: "i couldn't pull that from memory right now",
          },
        ]);
      }
    } finally {
      setAskSending(false);
    }
  }

  function renderPanel(): ReactNode {
    if (!detail) return null;
    // userTier gates what settings THIS admin can change.
    // serverTier reflects what features are currently active (from any paying admin).
    const tier = effectiveDashboardTier(me, detail);
    const goUpgrade = () => navigate("store");

    if (activeTab === "logs") return <LogsPanel serverId={detail.server.id} />;

    if (activeTab === "memory")
      return (
        <MemoryPanel
          lore={detail.lore}
          working={memoryWorking}
          error={memoryError}
          tier={tier}
          serverId={detail.server.id}
          channels={appChannels}
          scanLifecycle={detail.overview.scanLifecycle ?? emptyScanLifecycle}
          onAsk={() => setAskOpen(true)}
          onCreate={addMemory}
          onDelete={removeMemory}
          onUpgrade={goUpgrade}
          scanRunning={scanRunning}
          onScan={handleScan}
          onFullScan={handleFullScan}
          onCancelScan={handleCancelScan}
        />
      );

    if (activeTab === "recaps")
      return (
        <LockedSection
          requiredTier="index"
          currentTier={tier}
          onUpgrade={goUpgrade}
        >
          <RecapsPanel
            server={detail.server}
            saving={saving}
            saveError={saveError}
            onSave={saveServer}
          />
        </LockedSection>
      );

    if (activeTab === "privacy")
      return <PrivacyPanel server={detail.server} audit={detail.audit} />;

    if (activeTab === "settings")
      return (
        <LockedSection
          requiredTier="index"
          currentTier={tier}
          onUpgrade={goUpgrade}
        >
          <SettingsPanel
            server={detail.server}
            saving={saving}
            saveError={saveError}
            onSave={saveServer}
            onSavePatch={saveSettingsPatch}
            onUpgrade={goUpgrade}
          />
        </LockedSection>
      );

    if (activeTab === "profile")
      return (
        <LockedSection
          requiredTier="index"
          currentTier={tier}
          onUpgrade={goUpgrade}
        >
          <BotProfilePanel
            server={detail.server}
            userTier={tier}
            saving={saving}
            saveError={saveError}
            onSave={saveServer}
            onUpgrade={goUpgrade}
          />
        </LockedSection>
      );

    return (
      <OverviewPanel
        detail={detail}
        tier={tier}
        onNavigate={setActiveTab}
        onSavePatch={saveSettingsPatch}
        saving={saving}
        scanRunning={scanRunning}
        onScan={handleScan}
        onUpgrade={goUpgrade}
      />
    );
  }

  if (page === "oauth-callback") {
    return <OAuthCallbackPage />;
  }

  if (page === "auth-finish") {
    return <AuthFinishPage />;
  }

  if (page === "admin") {
    return (
      <AdminPage
        me={me}
        meLoading={loading}
        onNavigate={navigate}
        onOpenServer={(serverId) => {
          // Set the URL with the server ID BEFORE changing page so that
          // when getMe fires (triggered by the page change) it reads the
          // correct ?server= param and doesn't fall back to our own servers.
          window.history.pushState(
            null,
            "",
            `/dashboard?server=${serverId}#overview`,
          );
          setSelectedServerId(serverId);
          setPage("dashboard");
          setActiveTab("overview");
        }}
      />
    );
  }

  if (page === "servers") {
    return (
      <>
        <ServerManagementPage
          me={me}
          loading={loading}
          meLoadFailed={meLoadFailed}
          onNavigate={navigate}
          onManageServer={manageServer}
          onRelogin={() => void initiateLogin("/servers")}
        />
        {authError && (
          <AuthErrorToast
            dismissing={authToastDismissing}
            onDismiss={dismissAuthToast}
          />
        )}
      </>
    );
  }

  if (page === "store") {
    return (
      <>
        <BillingPage
          me={me}
          meLoading={loading}
          selectedServerId={selectedServerId}
          onNavigate={navigate}
          showToast={showToast}
        />
        {appToast && (
          <Toast
            message={appToast.message}
            tone={appToast.tone}
            icon={appToast.icon}
            dismissing={appToastDismissing}
            onDismiss={dismissAppToast}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="dashboard-outer">
        <div className="dashboard-nav-row">
          <MarketingNav me={me} meLoading={loading} onNavigate={navigate} />
        </div>
        <main className="shell">
          <aside className="sidebar" aria-label="Kodama dashboard navigation">
            {me && (
              <ServerSelect
                servers={me.servers}
                selectedId={selectedServerId}
                fallbackSelected={detail?.server ?? null}
                onChange={setSelectedServerId}
              />
            )}

            <nav className="nav" aria-label="Dashboard tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    className={`nav-item ${activeTab === tab.id ? "active" : ""}`}
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <Icon size={18} aria-hidden="true" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>

            <div className="sidebar-bottom">
              <button
                className="ghost-button"
                type="button"
                onClick={() => navigate("store")}
              >
                <CreditCard size={18} aria-hidden="true" />
                Store
              </button>

              <button
                className="ghost-button"
                type="button"
                onClick={() => {
                  logout().finally(() => {
                    window.location.href = marketingOrigin;
                  });
                }}
              >
                <LogOut size={18} />
                Log out
              </button>
            </div>
          </aside>

          <section className="content">
            <header className="topbar">
              <div>
                <p className="eyebrow">
                  {selectedServer?.name ?? detail?.server.name ?? "Kodama"}
                </p>
                <h2>{title}</h2>
              </div>
              <div className="refresh-block">
                <span>Last indexed</span>
                <strong>
                  {formatDate(detail?.overview.lastIndexedAt ?? null)}
                </strong>
              </div>
              <button
                className="primary-button"
                type="button"
                onClick={() =>
                  activeTab === "settings"
                    ? setActiveTab("overview")
                    : setActiveTab("settings")
                }
              >
                <SlidersHorizontal size={18} aria-hidden="true" />
                {activeTab === "settings" ? "Overview" : "Controls"}
              </button>
            </header>

            {me?.isAdmin &&
              detail &&
              !me.servers.some((s) => s.id === detail.server.id) && (
                <div className="admin-impersonation-banner">
                  <ShieldCheck size={14} aria-hidden="true" />
                  <span>
                    Admin view — <strong>{detail.server.name}</strong> (
                    {detail.server.id})
                  </span>
                  <button
                    type="button"
                    className="admin-impersonation-back"
                    onClick={() => navigate("admin")}
                  >
                    ← Back to admin panel
                  </button>
                </div>
              )}

            {loading && !detail ? (
              <EmptyState
                icon={<LoadingDots label="Loading dashboard" />}
                title="Loading dashboard"
                body="Checking your Discord servers and Kodama settings."
              />
            ) : dashboardError ? (
              <EmptyState
                title="Could not load this server"
                body={dashboardError}
                action={
                  <button
                    className="primary-button"
                    type="button"
                    onClick={() => setLoadAttempt((attempt) => attempt + 1)}
                  >
                    <SlidersHorizontal size={18} aria-hidden="true" />
                    Try again
                  </button>
                }
              />
            ) : !detail ? (
              <EmptyState
                title="No manageable servers"
                body="Install Kodama in a server where you have Manage Server permission, then reload."
                action={
                  <button
                    className="primary-button"
                    type="button"
                    onClick={() => void initiateLogin(currentReturnTo())}
                  >
                    <LogIn size={18} aria-hidden="true" />
                    Login with Discord
                  </button>
                }
              />
            ) : (
              renderPanel()
            )}
          </section>
        </main>
      </div>
      {authError && (
        <AuthErrorToast
          dismissing={authToastDismissing}
          onDismiss={dismissAuthToast}
        />
      )}
      {appToast && (
        <Toast
          message={appToast.message}
          tone={appToast.tone}
          icon={appToast.icon}
          dismissing={appToastDismissing}
          onDismiss={dismissAppToast}
        />
      )}
      {detail && (
        <AskKodamaWidget
          open={askOpen}
          serverName={detail.server.name}
          messages={askMessages}
          sending={askSending}
          error={askError}
          capped={askCapped}
          onOpen={() => setAskOpen(true)}
          onClose={() => setAskOpen(false)}
          onSend={sendAskQuestion}
          onUpgrade={() => {
            setAskOpen(false);
            navigate("store");
          }}
        />
      )}
      {upgradeModal && (
        <UpgradeModal
          requiredTier={upgradeModal.requiredTier}
          onClose={() => setUpgradeModal(null)}
          onGoToStore={() => {
            setUpgradeModal(null);
            navigate("store");
          }}
        />
      )}
    </>
  );
}

export function mountApp(): void {
  createRoot(document.getElementById("root") as HTMLElement).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
